import { makeStyles } from "@material-ui/core";
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { Select } from "antd";
import { REPORT_FONT, useReportFonts } from "../reportFonts";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";
import { useHistory, useParams } from "react-router-dom";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
  HorizontalDateRangeFilter,
  FilterApplyButton,
} from "./GlobalFilterPanel";
import { useExpandedAppliedFilters } from "./useExpandedAppliedFilters";
import {
  applyCascadeDraftChange,
  pruneInvalidCascadeSelections,
  getFilterFieldOptions,
  toFilterSelectOptions,
  FILTER_SELECT_SEARCH_PROPS,
  GlobalMultiSelectFilter,
} from "./filterRelationshipUtils";
import {
  getBudgetVsCurrentForecastAPI,
  getPriorVsCurrentForecastAPI,
  getScenarioMomNextYearForecastAPI,
  getCompareForecastVersionsAPI,
  getVersionsAPI,
  downloadBudgetVsCurrentForecastAPI,
  downloadPriorVsCurrentForecastAPI,
  downloadScenarioMomNextYearForecastAPI,
  downloadCompareForecastVersionsAPI,
} from "../../../api/reports-api";
import Loading from "../../../common-components/Loading";
import BudgetVsCurrentForecastTabComponent from "./scenario-mom/BudgetVsCurrentForecast";
import PriorVsCurrentForecastTabComponent from "./scenario-mom/PriorVsCurrentForecast";
import NextYearForecastTabComponent from "./scenario-mom/NextYearForecast";
import CompareForecastVersionsTabComponent from "./scenario-mom/CompareForecastVersions";
import {
  METRIC_BANDS,
  bandTotalForMonths,
  aggregateScenarioMomRow,
  fmtPctScenario,
  isDisplayNegative,
  DEFAULT_VISIBLE_BANDS,
} from "./scenario-mom/columnPickerShared";
import { hasAnyTableFilters } from "./tableTotalUtils";
import {
  calcFilterPos,
  calcOpDropdownPos,
  isBudgetVersion,
  resolveDefaultCompareVersion,
  FILTER_PANEL_WIDTH_NUMERIC,
} from "./waterfall-eac/filterPopoverUtils";
import { NumericFilterContent, TextFilterContent, OpDropdownContent } from "./waterfall-eac/tableColumnFilterShared";
import {
  buildWaterfallEacApiFilters,
  buildWaterfallEacDownloadFilters,
  getApiFilterKey,
  getGlobalPeriodColumnKeys,
} from "./waterfall-eac/waterfallEacFilterUtils";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
  cellWhiteSpaceStyle,
  dynamicColSize,
} from "./waterfall-eac/waterfallTableUtils";
import {
  usePlByProgramStyles,
  PlPageHeader,
} from "./pl-by-program/plByProgramTheme";

import {
  TH_BG,
  TH_BG_BAND,
  TH_TEXT,
  TH_BORDER,
  TH_ACTUAL_BG,
  TH_ACTUAL_TEXT,
  CELL_FG,
  CELL_FG_STRONG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG,
  TOTAL_BG_ALT,
  ROW_TOTAL_BG,
  ACCENT_COLOR,
  Z_HEAD,
  Z_STICKY,
} from "../reportTableConfig";

const HEADER_BG = TH_BG_BAND; /* Revenue band / row-1 header  */
const HEADER_COL = TH_BG; /* column sub-header row-2      */
const HEADER_DL = "#162032"; /* Direct Labor band            */

const TAB_ACTIVE = "#4F46E5";
const BORDER = "#E5E7EB";

/* ══════════════════════════════════════════════════════════════ */
/*  Tab definitions                                               */
/* ══════════════════════════════════════════════════════════════ */
const TABS = [
  { key: "budget_vs_forecast", label: "Budget Vs Current Forecast" },
  { key: "prior_vs_forecast", label: "Prior Vs Current Forecast" },
  { key: "next_year_forecast", label: "Next Year Forecast" },
  { key: "compare_versions", label: "Compare Forecast Versions Report" },
];

/* Per-tab download endpoint + output filename */
const DOWNLOAD_BY_TAB = {
  budget_vs_forecast: { api: downloadBudgetVsCurrentForecastAPI, filename: "budget-vs-current-forecast.xlsx" },
  prior_vs_forecast: { api: downloadPriorVsCurrentForecastAPI, filename: "prior-vs-current-forecast.xlsx" },
  next_year_forecast: { api: downloadScenarioMomNextYearForecastAPI, filename: "next-year-forecast.xlsx" },
  compare_versions: { api: downloadCompareForecastVersionsAPI, filename: "compare-forecast-versions.xlsx" },
};

/* ══════════════════════════════════════════════════════════════ */
/*  Month metadata                                                */
/* ══════════════════════════════════════════════════════════════ */
const SC_MONTHS = [
  { key: "jan", label: "Jan" },
  { key: "feb", label: "Feb" },
  { key: "mar", label: "Mar" },
  { key: "apr", label: "Apr" },
  { key: "may", label: "May" },
  { key: "jun", label: "Jun" },
  { key: "jul", label: "Jul" },
  { key: "aug", label: "Aug" },
  { key: "sep", label: "Sep" },
  { key: "oct", label: "Oct" },
  { key: "nov", label: "Nov" },
  { key: "dec", label: "Dec" },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Data — Current Month Actuals + Live Forecast                  */
/*  Each row has rev_* (Revenue) and dl_* (Direct Labor) keys     */
/* ══════════════════════════════════════════════════════════════ */
const CF_TOTAL = {
  /* Revenue */
  rev_jan: 30123884,
  rev_feb: 28699897,
  rev_mar: 29587933,
  rev_apr: 28856329,
  rev_may: 29031306,
  rev_jun: 30249498,
  rev_jul: 31278756,
  rev_aug: 31033247,
  rev_sep: 31831844,
  rev_oct: 32819277,
  rev_nov: 33280631,
  rev_dec: 36349650,
  /* Direct Labor */
  dl_jan: 11984848,
  dl_feb: 12448559,
  dl_mar: 12632355,
  dl_apr: 12064741,
  dl_may: 12142379,
  dl_jun: 11962748,
  dl_jul: 12118735,
  dl_aug: 12572191,
  dl_sep: 12538856,
  dl_oct: 12791015,
  dl_nov: 13024681,
  dl_dec: 13777902,
};

const CF_DATA = [
  {
    contractId: "01112",
    contractName: "EFIT",
    contractType: "FFP",
    popEndDate: "9/30/2025",
    rev_jan: null,
    rev_feb: null,
    rev_mar: 26678,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01115",
    contractName: "GSA DIGIT (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "1/4/2028",
    rev_jan: 388055,
    rev_feb: 287364,
    rev_mar: 375790,
    rev_apr: 377876,
    rev_may: 306716,
    rev_jun: 321969,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 181752,
    dl_feb: 188443,
    dl_mar: 192753,
    dl_apr: 195837,
    dl_may: 160764,
    dl_jun: 161091,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01117",
    contractName: "NAV E&I (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "6/30/2028",
    rev_jan: 37513,
    rev_feb: 30115,
    rev_mar: 43969,
    rev_apr: 30088,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 20840,
    dl_feb: 19052,
    dl_mar: 25726,
    dl_apr: 24959,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01122",
    contractName: "TSA ESVP POMS (FMT)",
    contractType: "T&M",
    popEndDate: "4/24/2028",
    rev_jan: 234763,
    rev_feb: 253882,
    rev_mar: 226628,
    rev_apr: 176547,
    rev_may: 114093,
    rev_jun: 109202,
    rev_jul: 114403,
    rev_aug: 109202,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 114297,
    dl_feb: 138474,
    dl_mar: 118704,
    dl_apr: 124415,
    dl_may: 105393,
    dl_jun: 37779,
    dl_jul: 26402,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01123",
    contractName: "USDA ELM 4 OLYMPIA (EHS)",
    contractType: "T&M",
    popEndDate: "7/15/2027",
    rev_jan: 185139,
    rev_feb: 162080,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 105537,
    dl_feb: 101751,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01124",
    contractName: "USDA ELM 4 TSPI",
    contractType: "HYBRID",
    popEndDate: "2/26/2026",
    rev_jan: 139937,
    rev_feb: 161015,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 143891,
    dl_feb: 133115,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01125",
    contractName: "CBP HIEOps",
    contractType: "T&M",
    popEndDate: "1/24/2028",
    rev_jan: 295356,
    rev_feb: 303697,
    rev_mar: 339024,
    rev_apr: 329894,
    rev_may: 255380,
    rev_jun: 273694,
    rev_jul: 290029,
    rev_aug: 279000,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 164148,
    dl_feb: 182232,
    dl_mar: 172957,
    dl_apr: 193626,
    dl_may: 163531,
    dl_jun: 167006,
    dl_jul: 148236,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01129",
    contractName: "DHS USCIS PMSS - CGI",
    contractType: "T&M",
    popEndDate: "5/14/2029",
    rev_jan: 668351,
    rev_feb: 712161,
    rev_mar: 761490,
    rev_apr: 769310,
    rev_may: 679514,
    rev_jun: 743206,
    rev_jul: 803190,
    rev_aug: 778000,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 331412,
    dl_feb: 390331,
    dl_mar: 383376,
    dl_apr: 383003,
    dl_may: 256598,
    dl_jun: 272837,
    dl_jul: 380674,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Data — Current Month Actuals + Forecast Q2 FY-2026           */
/* ══════════════════════════════════════════════════════════════ */
const Q2_TOTAL = {
  rev_jan: 30123884,
  rev_feb: 28899897,
  rev_mar: 29587933,
  rev_apr: 30403209,
  rev_may: 30791630,
  rev_jun: 32917818,
  rev_jul: 32751116,
  rev_aug: 35247709,
  rev_sep: 36810183,
  rev_oct: 37736774,
  rev_nov: 37638486,
  rev_dec: 40893835,
  dl_jan: 11984848,
  dl_feb: 12448559,
  dl_mar: 12832355,
  dl_apr: 12082310,
  dl_may: 12521342,
  dl_jun: 12982103,
  dl_jul: 12847273,
  dl_aug: 14234279,
  dl_sep: 14520279,
  dl_oct: 14817512,
  dl_nov: 14790802,
  dl_dec: 15605204,
};

const Q2_DATA = [
  {
    contractId: "01131",
    contractName: "FBI_RADIO_PRG_ENG TC SRV",
    contractType: "T&M",
    popEndDate: "7/30/2028",
    rev_jan: 28785,
    rev_feb: 35527,
    rev_mar: 42520,
    rev_apr: 39912,
    rev_may: 43920,
    rev_jun: 58320,
    rev_jul: 61097,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 12953,
    dl_feb: 15987,
    dl_mar: 19134,
    dl_apr: 17960,
    dl_may: 19764,
    dl_jun: 26244,
    dl_jul: 27494,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01134",
    contractName: "DOD NAWCWD CSSS (NDTI)",
    contractType: "T&M",
    popEndDate: "3/10/2029",
    rev_jan: 13296,
    rev_feb: 13690,
    rev_mar: 16645,
    rev_apr: 39065,
    rev_may: 35513,
    rev_jun: 37289,
    rev_jul: 39065,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 5983,
    dl_feb: 6161,
    dl_mar: 7490,
    dl_apr: 17579,
    dl_may: 15981,
    dl_jun: 16780,
    dl_jul: 17579,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01135",
    contractName: "BIXAL SEC BPA",
    contractType: "FFP",
    popEndDate: "12/17/2028",
    rev_jan: 92386,
    rev_feb: 87861,
    rev_mar: 105448,
    rev_apr: 87125,
    rev_may: 80634,
    rev_jun: 83880,
    rev_jul: 101734,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 41574,
    dl_feb: 39538,
    dl_mar: 47452,
    dl_apr: 39206,
    dl_may: 36285,
    dl_jun: 37746,
    dl_jul: 45780,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01137",
    contractName: "Tomahawk Tapestry Solutio",
    contractType: "T&M",
    popEndDate: "12/14/2026",
    rev_jan: 137363,
    rev_feb: 135720,
    rev_mar: 164448,
    rev_apr: 150060,
    rev_may: 122132,
    rev_jun: 128239,
    rev_jul: 134345,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 61813,
    dl_feb: 61074,
    dl_mar: 74002,
    dl_apr: 67527,
    dl_may: 54959,
    dl_jun: 57708,
    dl_jul: 60455,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01138",
    contractName: "MFIS CGM",
    contractType: "T&M",
    popEndDate: "9/30/2029",
    rev_jan: 216476,
    rev_feb: 205078,
    rev_mar: 207458,
    rev_apr: 207586,
    rev_may: 188714,
    rev_jun: 207586,
    rev_jul: 207586,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 97414,
    dl_feb: 92285,
    dl_mar: 93356,
    dl_apr: 93414,
    dl_may: 84921,
    dl_jun: 93414,
    dl_jul: 93414,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01140",
    contractName: "FBI SOAPS TO106BRDG",
    contractType: "HYBRID",
    popEndDate: "4/30/2026",
    rev_jan: 135009,
    rev_feb: 139733,
    rev_mar: 177525,
    rev_apr: 117369,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 60754,
    dl_feb: 62880,
    dl_mar: 79886,
    dl_apr: 52816,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Data — Compare Forecast Versions — Variance (CF − Q1)         */
/* ══════════════════════════════════════════════════════════════ */
const CMP_VAR_TOTAL = {
  rev_jan: 546794,
  rev_feb: 701700,
  rev_mar: 2089850,
  rev_apr: 2277516,
  rev_may: 1885505,
  rev_jun: 172141,
  rev_jul: -834477,
  rev_aug: -651130,
  rev_sep: -642127,
  rev_oct: -249197,
  rev_nov: -203798,
  rev_dec: -413983,
  dl_jan: 0,
  dl_feb: 0,
  dl_mar: 0,
  dl_apr: 0,
  dl_may: -801556,
  dl_jun: -929355,
  dl_jul: -728538,
  dl_aug: null,
  dl_sep: null,
  dl_oct: null,
  dl_nov: null,
  dl_dec: null,
};

const CMP_VAR_DATA = [
  {
    contractId: "01112",
    contractName: "EFIT",
    contractType: "FFP",
    popEndDate: "9/30/2025",
    rev_jan: null,
    rev_feb: null,
    rev_mar: 26678,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01115",
    contractName: "GSA DIGIT (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "1/4/2028",
    rev_jan: 39920,
    rev_feb: -36401,
    rev_mar: 904,
    rev_apr: 2990,
    rev_may: -34090,
    rev_jun: -35877,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 0,
    dl_feb: 0,
    dl_mar: 0,
    dl_apr: 0,
    dl_may: -3298,
    dl_jun: -3662,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01117",
    contractName: "NAV E&I (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "6/30/2028",
    rev_jan: 1053,
    rev_feb: -10220,
    rev_mar: -2734,
    rev_apr: 30088,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 0,
    dl_feb: 0,
    dl_mar: 0,
    dl_apr: 13540,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01122",
    contractName: "TSA ESVP POMS (FMT)",
    contractType: "T&M",
    popEndDate: "4/24/2028",
    rev_jan: -677,
    rev_feb: 17843,
    rev_mar: -53068,
    rev_apr: -116552,
    rev_may: -152360,
    rev_jun: -170574,
    rev_jul: -178696,
    rev_aug: -170573,
    rev_sep: -170573,
    rev_oct: -170573,
    rev_nov: -154328,
    rev_dec: -178696,
    dl_jan: 0,
    dl_feb: 0,
    dl_mar: 0,
    dl_apr: 0,
    dl_may: -3678,
    dl_jun: -8379,
    dl_jul: -8919,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01123",
    contractName: "USDA ELM 4 OLYMPIA (EHS)",
    contractType: "T&M",
    popEndDate: "7/15/2027",
    rev_jan: 12576,
    rev_feb: -1218,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 0,
    dl_feb: 0,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01124",
    contractName: "USDA ELM 4 TSPI",
    contractType: "HYBRID",
    popEndDate: "2/26/2026",
    rev_jan: -10539,
    rev_feb: 10539,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 0,
    dl_feb: 0,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01125",
    contractName: "CBP HIEOps",
    contractType: "T&M",
    popEndDate: "1/24/2028",
    rev_jan: 9039,
    rev_feb: 26444,
    rev_mar: 17994,
    rev_apr: -124,
    rev_may: -44636,
    rev_jun: -41323,
    rev_jul: -39989,
    rev_aug: -15583,
    rev_sep: -15583,
    rev_oct: -15583,
    rev_nov: -14099,
    rev_dec: -16325,
    dl_jan: 0,
    dl_feb: 0,
    dl_mar: 0,
    dl_apr: 0,
    dl_may: -3079,
    dl_jun: -1838,
    dl_jul: -2487,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01128",
    contractName: "DHS USCIS RMSS - CGI",
    contractType: "T&M",
    popEndDate: "5/14/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: -32147,
    rev_jul: -8996,
    rev_aug: -2837,
    rev_sep: -2837,
    rev_oct: -2837,
    rev_nov: -2567,
    rev_dec: -2972,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01129",
    contractName: "USARC CYBER SERV (INDIGO)",
    contractType: "T&M",
    popEndDate: "7/12/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Data — Compare Forecast Versions — Q1 Forecast                */
/* ══════════════════════════════════════════════════════════════ */
const CMP_Q1_TOTAL = {
  rev_jan: 29577091,
  rev_feb: 27998197,
  rev_mar: 27498083,
  rev_apr: 26578813,
  rev_may: 27145802,
  rev_jun: 30077357,
  rev_jul: 32113233,
  rev_aug: 31033247,
  rev_sep: null,
  rev_oct: null,
  rev_nov: null,
  rev_dec: null,
  dl_jan: 11984848,
  dl_feb: 12448559,
  dl_mar: 12832355,
  dl_apr: 12082310,
  dl_may: 12943935,
  dl_jun: 12892103,
  dl_jul: 12847273,
  dl_aug: null,
  dl_sep: null,
  dl_oct: null,
  dl_nov: null,
  dl_dec: null,
};

const CMP_Q1_DATA = [
  {
    contractId: "01115",
    contractName: "GSA DIGIT (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "1/4/2028",
    rev_jan: 348135,
    rev_feb: 323765,
    rev_mar: 374886,
    rev_apr: 374886,
    rev_may: 340806,
    rev_jun: 357846,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 174625,
    dl_feb: 129314,
    dl_mar: 169106,
    dl_apr: 170044,
    dl_may: 141320,
    dl_jun: 148548,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01117",
    contractName: "NAV E&I (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "6/30/2028",
    rev_jan: 36460,
    rev_feb: 40335,
    rev_mar: 46703,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 16881,
    dl_feb: 13552,
    dl_mar: 19786,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01122",
    contractName: "TSA ESVP POMS (FMT)",
    contractType: "T&M",
    popEndDate: "4/24/2028",
    rev_jan: 235440,
    rev_feb: 236039,
    rev_mar: 279696,
    rev_apr: 293099,
    rev_may: 266453,
    rev_jun: 279776,
    rev_jul: 293099,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 105643,
    dl_feb: 114247,
    dl_mar: 101983,
    dl_apr: 79446,
    dl_may: 55020,
    dl_jun: 57520,
    dl_jul: 60400,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01123",
    contractName: "USDA ELM 4 OLYMPIA (EHS)",
    contractType: "T&M",
    popEndDate: "7/15/2027",
    rev_jan: 172563,
    rev_feb: 163298,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 83313,
    dl_feb: 72936,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01124",
    contractName: "USDA ELM 4 TSPI",
    contractType: "HYBRID",
    popEndDate: "2/26/2026",
    rev_jan: 150476,
    rev_feb: 150476,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 62972,
    dl_feb: 72457,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01125",
    contractName: "CBP HIEOps",
    contractType: "T&M",
    popEndDate: "1/24/2028",
    rev_jan: 286316,
    rev_feb: 277254,
    rev_mar: 321030,
    rev_apr: 330018,
    rev_may: 300016,
    rev_jun: 315017,
    rev_jul: 330018,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 132910,
    dl_feb: 136664,
    dl_mar: 152561,
    dl_apr: 148452,
    dl_may: 118000,
    dl_jun: 125000,
    dl_jul: 133000,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Data — Prior Vs Current Variance (Live − Q2)                  */
/* ══════════════════════════════════════════════════════════════ */
const PRIOR_VAR_TOTAL = {
  rev_jan: null,
  rev_feb: null,
  rev_mar: null,
  rev_apr: -1546879,
  rev_may: -1760324,
  rev_jun: -2668320,
  rev_jul: -1472360,
  rev_aug: -4214462,
  rev_sep: -4978339,
  rev_oct: -5117496,
  rev_nov: -4377000,
  rev_dec: null,
  dl_jan: null,
  dl_feb: null,
  dl_mar: null,
  dl_apr: -17631,
  dl_may: -378963,
  dl_jun: -1019355,
  dl_jul: -728574,
  dl_aug: -1461588,
  dl_sep: -1981423,
  dl_oct: -2026497,
  dl_nov: -1765121,
  dl_dec: null,
};

const PRIOR_VAR_DATA = [
  {
    contractId: "01115",
    contractName: "GSA DIGIT (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "1/4/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: -3607,
    rev_may: -40045,
    rev_jun: -42037,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: -1623,
    dl_may: -18020,
    dl_jun: -18917,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01117",
    contractName: "NAV E&I (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "6/30/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: 30088,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: 13540,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01122",
    contractName: "TSA ESVP POMS (FMT)",
    contractType: "T&M",
    popEndDate: "4/24/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: 6932,
    rev_may: 55216,
    rev_jun: 53068,
    rev_jul: 55596,
    rev_aug: 53068,
    rev_sep: 53068,
    rev_oct: 53068,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: 3119,
    dl_may: 24847,
    dl_jun: 23881,
    dl_jul: 25018,
    dl_aug: 23881,
    dl_sep: 23881,
    dl_oct: 23881,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01125",
    contractName: "CBP HIEOps",
    contractType: "T&M",
    popEndDate: "1/24/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: 8864,
    rev_may: -36466,
    rev_jun: -32744,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: 3989,
    dl_may: -16410,
    dl_jun: -14735,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01128",
    contractName: "DHS USCIS RMSS - CGI",
    contractType: "T&M",
    popEndDate: "5/14/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: 29806,
    rev_may: 4063,
    rev_jun: 2963,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01129",
    contractName: "USARC CYBER SERV (INDIGO)",
    contractType: "T&M",
    popEndDate: "7/12/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: 2408,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01130",
    contractName: "USAF CAMPS SUST (NETRIST)",
    contractType: "T&M",
    popEndDate: "12/31/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: 22951,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01131",
    contractName: "FBI_RADIO_PRG_ENG TC SRV",
    contractType: "T&M",
    popEndDate: "7/30/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: 2602,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Data — Next Year Forecast (FY-2027)                           */
/* ══════════════════════════════════════════════════════════════ */
const NY_TOTAL = {
  rev_jan: 30123884,
  rev_feb: 28699897,
  rev_mar: 29587933,
  rev_apr: 28856329,
  rev_may: 29031306,
  rev_jun: 30249498,
  rev_jul: 31278756,
  rev_aug: 31033247,
  rev_sep: 31831844,
  rev_oct: 32819277,
  rev_nov: 33280631,
  rev_dec: 36349650,
  dl_jan: 11984848,
  dl_feb: 12448559,
  dl_mar: 12632355,
  dl_apr: 12064741,
  dl_may: 12142379,
  dl_jun: 11962748,
  dl_jul: 12118735,
  dl_aug: 12572191,
  dl_sep: 12538856,
  dl_oct: 12791015,
  dl_nov: 13024681,
  dl_dec: 13777902,
};

const NY_DATA = [
  {
    contractId: "01112",
    contractName: "EFIT",
    contractType: "FFP",
    popEndDate: "9/30/2025",
    rev_jan: null,
    rev_feb: null,
    rev_mar: 26678,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01115",
    contractName: "GSA DIGIT (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "1/4/2028",
    rev_jan: 388055,
    rev_feb: 287364,
    rev_mar: 375790,
    rev_apr: 377876,
    rev_may: 306716,
    rev_jun: 321969,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 174625,
    dl_feb: 129314,
    dl_mar: 169106,
    dl_apr: 170044,
    dl_may: 138022,
    dl_jun: 144886,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01117",
    contractName: "NAV E&I (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "6/30/2028",
    rev_jan: 37513,
    rev_feb: 30115,
    rev_mar: 43969,
    rev_apr: 30088,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 16881,
    dl_feb: 13552,
    dl_mar: 19786,
    dl_apr: 13540,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01122",
    contractName: "TSA ESVP POMS (FMT)",
    contractType: "T&M",
    popEndDate: "4/24/2028",
    rev_jan: 234763,
    rev_feb: 253882,
    rev_mar: 226628,
    rev_apr: 176547,
    rev_may: 114093,
    rev_jun: 109202,
    rev_jul: 114403,
    rev_aug: 109202,
    rev_sep: 109202,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 105643,
    dl_feb: 114247,
    dl_mar: 101983,
    dl_apr: 79446,
    dl_may: 51342,
    dl_jun: 49141,
    dl_jul: 51481,
    dl_aug: 49141,
    dl_sep: 49141,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01123",
    contractName: "USDA ELM 4 OLYMPIA (EHS)",
    contractType: "T&M",
    popEndDate: "7/15/2027",
    rev_jan: 185139,
    rev_feb: 162080,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 83313,
    dl_feb: 72936,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01124",
    contractName: "USDA ELM 4 TSPI",
    contractType: "HYBRID",
    popEndDate: "2/26/2026",
    rev_jan: 139937,
    rev_feb: 161015,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 62972,
    dl_feb: 72457,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01125",
    contractName: "CBP HIEOps",
    contractType: "T&M",
    popEndDate: "1/24/2028",
    rev_jan: 295356,
    rev_feb: 303697,
    rev_mar: 339024,
    rev_apr: 329894,
    rev_may: 255380,
    rev_jun: 273694,
    rev_jul: 290029,
    rev_aug: 276846,
    rev_sep: 276846,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 132910,
    dl_feb: 136664,
    dl_mar: 152561,
    dl_apr: 148452,
    dl_may: 114921,
    dl_jun: 123162,
    dl_jul: 130513,
    dl_aug: 124581,
    dl_sep: 124581,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01128",
    contractName: "DHS USCIS RMSS - CGI",
    contractType: "T&M",
    popEndDate: "5/14/2028",
    rev_jan: 668351,
    rev_feb: 712161,
    rev_mar: 761490,
    rev_apr: 769210,
    rev_may: 679514,
    rev_jun: 743206,
    rev_jul: 803190,
    rev_aug: 772517,
    rev_sep: 772517,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 300758,
    dl_feb: 320472,
    dl_mar: 342671,
    dl_apr: 346145,
    dl_may: 305781,
    dl_jun: 334443,
    dl_jul: 361436,
    dl_aug: 347633,
    dl_sep: 347633,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01129",
    contractName: "USARC CYBER SERV (INDIGO)",
    contractType: "T&M",
    popEndDate: "7/12/2028",
    rev_jan: 63134,
    rev_feb: 62201,
    rev_mar: 70282,
    rev_apr: 71874,
    rev_may: 63151,
    rev_jun: 66308,
    rev_jul: 58376,
    rev_aug: 66072,
    rev_sep: 66072,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 28410,
    dl_feb: 27990,
    dl_mar: 31627,
    dl_apr: 32343,
    dl_may: 28418,
    dl_jun: 29839,
    dl_jul: 26269,
    dl_aug: 29732,
    dl_sep: 29732,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01130",
    contractName: "USAF CAMPS SUST (NETRIST)",
    contractType: "T&M",
    popEndDate: "12/31/2028",
    rev_jan: 11198,
    rev_feb: 146128,
    rev_mar: 86244,
    rev_apr: 105799,
    rev_may: 75317,
    rev_jun: 82848,
    rev_jul: 82848,
    rev_aug: 79082,
    rev_sep: 79082,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 5039,
    dl_feb: 65758,
    dl_mar: 38810,
    dl_apr: 47610,
    dl_may: 33893,
    dl_jun: 37282,
    dl_jul: 37282,
    dl_aug: 35587,
    dl_sep: 35587,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01131",
    contractName: "FBI_RADIO_PRG_ENG TC SRV",
    contractType: "T&M",
    popEndDate: "7/30/2028",
    rev_jan: 28785,
    rev_feb: 35527,
    rev_mar: 42520,
    rev_apr: 42513,
    rev_may: 43920,
    rev_jun: 58320,
    rev_jul: 61097,
    rev_aug: 58517,
    rev_sep: 58517,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 12953,
    dl_feb: 15987,
    dl_mar: 19134,
    dl_apr: 19131,
    dl_may: 19764,
    dl_jun: 26244,
    dl_jul: 27494,
    dl_aug: 26333,
    dl_sep: 26333,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01134",
    contractName: "DOD NAWCWD CSSS (NDTI)",
    contractType: "T&M",
    popEndDate: "3/10/2029",
    rev_jan: 13296,
    rev_feb: 13690,
    rev_mar: 16645,
    rev_apr: 15758,
    rev_may: 35513,
    rev_jun: 37289,
    rev_jul: 39065,
    rev_aug: 37289,
    rev_sep: 38008,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 5983,
    dl_feb: 6161,
    dl_mar: 7490,
    dl_apr: 7091,
    dl_may: 15981,
    dl_jun: 16780,
    dl_jul: 17579,
    dl_aug: 16780,
    dl_sep: 17104,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01135",
    contractName: "BIXAL SEC BPA",
    contractType: "FFP",
    popEndDate: "12/17/2028",
    rev_jan: 92386,
    rev_feb: 87861,
    rev_mar: 105448,
    rev_apr: 101734,
    rev_may: 80634,
    rev_jun: 83880,
    rev_jul: 101734,
    rev_aug: 97109,
    rev_sep: 90619,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 41574,
    dl_feb: 39538,
    dl_mar: 47452,
    dl_apr: 45780,
    dl_may: 36285,
    dl_jun: 37746,
    dl_jul: 45780,
    dl_aug: 43699,
    dl_sep: 40779,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01137",
    contractName: "Tomahawk Tapestry Solutio",
    contractType: "T&M",
    popEndDate: "12/14/2026",
    rev_jan: 137363,
    rev_feb: 135720,
    rev_mar: 164448,
    rev_apr: 148392,
    rev_may: 122132,
    rev_jun: 128239,
    rev_jul: 134345,
    rev_aug: 128239,
    rev_sep: 128239,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 61813,
    dl_feb: 61074,
    dl_mar: 74002,
    dl_apr: 66776,
    dl_may: 54959,
    dl_jun: 57708,
    dl_jul: 60455,
    dl_aug: 57708,
    dl_sep: 57708,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01138",
    contractName: "MEIS SOM",
    contractType: "T&M",
    popEndDate: "9/30/2029",
    rev_jan: 216476,
    rev_feb: 205078,
    rev_mar: 207458,
    rev_apr: 183023,
    rev_may: 188714,
    rev_jun: 207586,
    rev_jul: 207586,
    rev_aug: 198150,
    rev_sep: 220800,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 97414,
    dl_feb: 92285,
    dl_mar: 93356,
    dl_apr: 82360,
    dl_may: 84921,
    dl_jun: 93414,
    dl_jul: 93414,
    dl_aug: 89168,
    dl_sep: 99360,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01140",
    contractName: "FBI_SOAPS_TO106BRDG",
    contractType: "HYBRID",
    popEndDate: "4/30/2026",
    rev_jan: 135009,
    rev_feb: 139733,
    rev_mar: 177525,
    rev_apr: 74053,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 60754,
    dl_feb: 62880,
    dl_mar: 79886,
    dl_apr: 33324,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01141",
    contractName: "SN.AF.ESS",
    contractType: "T&M",
    popEndDate: "3/31/2030",
    rev_jan: 141493,
    rev_feb: 128737,
    rev_mar: 157654,
    rev_apr: 137409,
    rev_may: 133362,
    rev_jun: 146788,
    rev_jul: 146788,
    rev_aug: 139628,
    rev_sep: 139628,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 63672,
    dl_feb: 57932,
    dl_mar: 70944,
    dl_apr: 61834,
    dl_may: 60013,
    dl_jun: 66055,
    dl_jul: 66055,
    dl_aug: 62833,
    dl_sep: 62833,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01143",
    contractName: "DOS_OBO_CTO_FOLLOW ...",
    contractType: "T&M",
    popEndDate: "9/17/2026",
    rev_jan: 60117,
    rev_feb: 54613,
    rev_mar: 46849,
    rev_apr: 46245,
    rev_may: 41878,
    rev_jun: 43972,
    rev_jul: 46066,
    rev_aug: 43972,
    rev_sep: 25120,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 27053,
    dl_feb: 24576,
    dl_mar: 21082,
    dl_apr: 20810,
    dl_may: 18845,
    dl_jun: 19787,
    dl_jul: 20730,
    dl_aug: 19787,
    dl_sep: 11304,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01144",
    contractName: "VA CDS (COFORMA)",
    contractType: "T&M",
    popEndDate: "9/8/2027",
    rev_jan: 41338,
    rev_feb: 35008,
    rev_mar: 48020,
    rev_apr: 48020,
    rev_may: 40599,
    rev_jun: 42629,
    rev_jul: 44658,
    rev_aug: 42629,
    rev_sep: 43278,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 18602,
    dl_feb: 15754,
    dl_mar: 21609,
    dl_apr: 21609,
    dl_may: 18270,
    dl_jun: 19183,
    dl_jul: 20096,
    dl_aug: 19183,
    dl_sep: 19475,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01145",
    contractName: "FBI_ITSSS_STOR184 BRDG",
    contractType: "T&M",
    popEndDate: "6/30/2026",
    rev_jan: 758,
    rev_feb: 10010,
    rev_mar: 4852,
    rev_apr: 13347,
    rev_may: 11223,
    rev_jun: 11829,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 341,
    dl_feb: 4505,
    dl_mar: 2183,
    dl_apr: 6006,
    dl_may: 5050,
    dl_jun: 5323,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01147",
    contractName: "P1 PB DSO SPT",
    contractType: "T&M",
    popEndDate: "11/11/2026",
    rev_jan: 67415,
    rev_feb: 64927,
    rev_mar: 68045,
    rev_apr: 75178,
    rev_may: 66548,
    rev_jun: 69875,
    rev_jul: 73203,
    rev_aug: 69875,
    rev_sep: 69875,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 30337,
    dl_feb: 29217,
    dl_mar: 30620,
    dl_apr: 33830,
    dl_may: 29947,
    dl_jun: 31444,
    dl_jul: 32941,
    dl_aug: 31444,
    dl_sep: 31444,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01149",
    contractName: "SOCOM DTS (IEX)",
    contractType: "T&M",
    popEndDate: "11/24/2026",
    rev_jan: 15075,
    rev_feb: 15075,
    rev_mar: 15913,
    rev_apr: 16750,
    rev_may: 9213,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 6784,
    dl_feb: 6784,
    dl_mar: 7161,
    dl_apr: 7538,
    dl_may: 4146,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01150",
    contractName: "FBI_CODIS STOR180",
    contractType: "T&M",
    popEndDate: "12/16/2026",
    rev_jan: 115946,
    rev_feb: 121503,
    rev_mar: 127590,
    rev_apr: 111945,
    rev_may: 109349,
    rev_jun: 133201,
    rev_jul: 139544,
    rev_aug: 133201,
    rev_sep: 133201,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 52176,
    dl_feb: 54676,
    dl_mar: 57416,
    dl_apr: 50375,
    dl_may: 49207,
    dl_jun: 59940,
    dl_jul: 62795,
    dl_aug: 59940,
    dl_sep: 59940,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01152",
    contractName: "IBP (LEIDOS)",
    contractType: "T&M",
    popEndDate: "5/31/2026",
    rev_jan: 69269,
    rev_feb: 68652,
    rev_mar: 81339,
    rev_apr: 78806,
    rev_may: 69839,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 31171,
    dl_feb: 30893,
    dl_mar: 36603,
    dl_apr: 35463,
    dl_may: 31428,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01153",
    contractName: "CBP ENAESS 3 BRIDGE",
    contractType: "T&M",
    popEndDate: "5/5/2026",
    rev_jan: 15965,
    rev_feb: 15965,
    rev_mar: 18485,
    rev_apr: 18485,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 7184,
    dl_feb: 7184,
    dl_mar: 8318,
    dl_apr: 8318,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Data — Budget                                                 */
/* ══════════════════════════════════════════════════════════════ */
const BDG_TOTAL = {
  /* Revenue */
  rev_jan: null,
  rev_feb: null,
  rev_mar: 30623761,
  rev_apr: 33631584,
  rev_may: 34424443,
  rev_jun: 35577351,
  rev_jul: 35878568,
  rev_aug: 33997585,
  rev_sep: 36722225,
  rev_oct: 35263000,
  rev_nov: null,
  rev_dec: null,
  /* Direct Labor */
  dl_jan: null,
  dl_feb: null,
  dl_mar: null,
  dl_apr: null,
  dl_may: 14003273,
  dl_jun: 14585767,
  dl_jul: 14806264,
  dl_aug: 13779814,
  dl_sep: 14067735,
  dl_oct: 14146875,
  dl_nov: 13265921,
  dl_dec: 14000000,
};

const BDG_DATA = [
  {
    contractId: "01115",
    contractName: "GSA DIGIT (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "1/4/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: 374886,
    rev_apr: 374886,
    rev_may: 340806,
    rev_jun: 357846,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: 185678,
    dl_jun: 194948,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01117",
    contractName: "NAV E&I (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "6/30/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: 46703,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01122",
    contractName: "TSA ESVP POMS (FMT)",
    contractType: "T&M",
    popEndDate: "4/24/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: 287632,
    rev_apr: 287632,
    rev_may: 261483,
    rev_jun: 274557,
    rev_jul: 287632,
    rev_aug: 274557,
    rev_sep: 274557,
    rev_oct: 274557,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: 141661,
    dl_jun: 148548,
    dl_jul: 155436,
    dl_aug: 148548,
    dl_sep: 148548,
    dl_oct: 148548,
    dl_nov: 134773,
    dl_dec: null,
  },
  {
    contractId: "01123",
    contractName: "USDA ELM 4 OLYMPIA (EHS)",
    contractType: "T&M",
    popEndDate: "7/15/2027",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01124",
    contractName: "USDA ELM 4 TSPI",
    contractType: "HYBRID",
    popEndDate: "2/26/2026",
    rev_jan: null,
    rev_feb: null,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01125",
    contractName: "CBP HIEOps",
    contractType: "T&M",
    popEndDate: "1/24/2028",
    rev_jan: null,
    rev_feb: null,
    rev_mar: 347599,
    rev_apr: 323936,
    rev_may: 294487,
    rev_jun: 309212,
    rev_jul: 323936,
    rev_aug: 286624,
    rev_sep: 286624,
    rev_oct: 286624,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: 198120,
    dl_jun: 207962,
    dl_jul: 217803,
    dl_aug: 192680,
    dl_sep: 192680,
    dl_oct: 192680,
    dl_nov: 174452,
    dl_dec: null,
  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Data — Variance (Current Forecast − Budget)                   */
/* ══════════════════════════════════════════════════════════════ */
const VAR_TOTAL = {
  rev_jan: 546794,
  rev_feb: 701700,
  rev_mar: 2089850,
  rev_apr: 2277516,
  rev_may: 1885505,
  rev_jun: 172141,
  rev_jul: -834477,
  rev_aug: null,
  rev_sep: null,
  rev_oct: null,
  rev_nov: null,
  rev_dec: null,
  dl_jan: 11984848,
  dl_feb: 12448559,
  dl_mar: 12632355,
  dl_apr: 12064741,
  dl_may: -1860894,
  dl_jun: -2623019,
  dl_jul: -2687529,
  dl_aug: null,
  dl_sep: null,
  dl_oct: null,
  dl_nov: null,
  dl_dec: null,
};

const VAR_DATA = [
  {
    contractId: "01112",
    contractName: "EFIT",
    contractType: "FFP",
    popEndDate: "9/30/2025",
    rev_jan: null,
    rev_feb: null,
    rev_mar: 26678,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01115",
    contractName: "GSA DIGIT (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "1/4/2028",
    rev_jan: 39920,
    rev_feb: -36401,
    rev_mar: 904,
    rev_apr: 2990,
    rev_may: -34090,
    rev_jun: -35877,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 181752,
    dl_feb: 188443,
    dl_mar: 192753,
    dl_apr: 195837,
    dl_may: -24914,
    dl_jun: -33857,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01117",
    contractName: "NAV E&I (SUB TO NCI)",
    contractType: "T&M",
    popEndDate: "6/30/2028",
    rev_jan: 1053,
    rev_feb: -10219,
    rev_mar: -2734,
    rev_apr: 30088,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 20840,
    dl_feb: 19052,
    dl_mar: 25726,
    dl_apr: 24959,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01122",
    contractName: "TSA ESVP POMS (FMT)",
    contractType: "T&M",
    popEndDate: "4/24/2028",
    rev_jan: -678,
    rev_feb: 17844,
    rev_mar: -53068,
    rev_apr: -116552,
    rev_may: -152360,
    rev_jun: -170573,
    rev_jul: -178696,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 114297,
    dl_feb: 138474,
    dl_mar: 118704,
    dl_apr: 124415,
    dl_may: -36268,
    dl_jun: -110769,
    dl_jul: -129034,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01123",
    contractName: "USDA ELM 4 OLYMPIA (EHS)",
    contractType: "T&M",
    popEndDate: "7/15/2027",
    rev_jan: 12576,
    rev_feb: -1218,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 105537,
    dl_feb: 101751,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01124",
    contractName: "USDA ELM 4 TSPI",
    contractType: "HYBRID",
    popEndDate: "2/26/2026",
    rev_jan: -10539,
    rev_feb: 10539,
    rev_mar: null,
    rev_apr: null,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 143891,
    dl_feb: 133115,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01125",
    contractName: "CBP HIEOps",
    contractType: "T&M",
    popEndDate: "1/24/2028",
    rev_jan: 9039,
    rev_feb: 26444,
    rev_mar: 17994,
    rev_apr: -124,
    rev_may: -44636,
    rev_jun: -41323,
    rev_jul: -39989,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: 164148,
    dl_feb: 182232,
    dl_mar: 172957,
    dl_apr: 193626,
    dl_may: -34589,
    dl_jun: -40956,
    dl_jul: -69567,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01128",
    contractName: "DHS USCIS RMSS - CGI",
    contractType: "T&M",
    popEndDate: "5/14/2028",
    rev_jan: -93185,
    rev_feb: 49863,
    rev_mar: -41785,
    rev_apr: -42977,
    rev_may: -59008,
    rev_jun: -32147,
    rev_jul: -8996,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
  {
    contractId: "01129",
    contractName: "USARC CYBER SERV (INDIGO)",
    contractType: "T&M",
    popEndDate: "7/12/2028",
    rev_jan: -17,
    rev_feb: 2208,
    rev_mar: 816,
    rev_apr: 2408,
    rev_may: null,
    rev_jun: null,
    rev_jul: null,
    rev_aug: null,
    rev_sep: null,
    rev_oct: null,
    rev_nov: null,
    rev_dec: null,
    dl_jan: null,
    dl_feb: null,
    dl_mar: null,
    dl_apr: null,
    dl_may: null,
    dl_jun: null,
    dl_jul: null,
    dl_aug: null,
    dl_sep: null,
    dl_oct: null,
    dl_nov: null,
    dl_dec: null,
  },
];

const fmtRev = (v) => {
  if (v === null || v === undefined) return "";
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "$0";
  if (n < 0) return `($${Math.abs(n).toLocaleString("en-US")})`;
  return `$${n.toLocaleString("en-US")}`;
};

const fmtVar = (v) => {
  if (v === null || v === undefined) return "";
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "$0";
  if (n < 0) return `($${Math.abs(n).toLocaleString("en-US")})`;
  return `$${n.toLocaleString("en-US")}`;
};

const bandTotal = (row, prefix) =>
  SC_MONTHS.reduce((sum, m) => sum + (row[`${prefix}_${m.key}`] ?? 0), 0);

/* ══ Table-filter helpers ══ */
const CT_OP_ENTRIES = [
  { op: "gt", sym: ">", label: "Greater than" },
  { op: "gte", sym: "≥", label: "At least" },
  { op: "lt", sym: "<", label: "Less than" },
  { op: "lte", sym: "≤", label: "At most" },
  { op: "eq", sym: "=", label: "Equal to" },
  { op: "neq", sym: "≠", label: "Not equal to" },
];

function ctApplyNumFilter(v, f) {
  if (!f?.value) return true;
  const n = Number(v),
    fv = Number(f.value);
  if (isNaN(n) || isNaN(fv)) return false;
  switch (f.op) {
    case "gt":
      return n > fv;
    case "gte":
      return n >= fv;
    case "lt":
      return n < fv;
    case "lte":
      return n <= fv;
    case "eq":
      return n === fv;
    case "neq":
      return n !== fv;
    default:
      return true;
  }
}

const fpBase = {
  position: "fixed",
  zIndex: 9999,
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.13)",
  padding: "12px",
  minWidth: 220,
};
const fpLbl = {
  fontSize: 10,
  fontWeight: 700,
  color: "#6B7280",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontFamily: REPORT_FONT,
  marginBottom: 8,
  display: "block",
};
const fpTxtI = {
  width: "100%",
  boxSizing: "border-box",
  height: 28,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  padding: "0 8px",
  fontSize: 12,
  color: "#374151",
  fontFamily: REPORT_FONT,
  outline: "none",
};
const fpNumI = {
  flex: 1,
  height: 28,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  padding: "0 8px",
  fontSize: 12,
  color: "#4F46E5",
  fontFamily: REPORT_FONT,
  outline: "none",
  minWidth: 0,
};
const fpOpBt = {
  width: 28,
  height: 28,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  background: "#fff",
  color: "#4F46E5",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontFamily: REPORT_FONT,
};
const fpClr = {
  marginTop: 8,
  fontSize: 11,
  color: "#94A3B8",
  cursor: "pointer",
  background: "none",
  border: "none",
  padding: 0,
  fontFamily: REPORT_FONT,
  textDecoration: "underline",
  display: "block",
};
const opDropS = {
  position: "fixed",
  zIndex: 10000,
  background: "#fff",
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
  overflow: "hidden",
  minWidth: 190,
};
const opItm = (a) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "7px 12px",
  fontSize: 12,
  cursor: "pointer",
  fontFamily: REPORT_FONT,
  background: a ? "#F5F3FF" : "transparent",
  color: a ? "#4F46E5" : "#374151",
  fontWeight: a ? 600 : 400,
});
const ctFBtnSt = (a, isActual = false) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  padding: 0,
  borderRadius: 3,
  border: "none",
  cursor: "pointer",
  background: a
    ? isActual
      ? "rgba(0,0,0,0.12)"
      : "rgba(165,180,252,0.18)"
    : "transparent",
  color: isActual ? TH_ACTUAL_TEXT : a ? "#A5B4FC" : TH_TEXT,
  opacity: a || isActual ? 1 : 0.5,
  flexShrink: 0,
});

const ctPagBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 20px",
  borderTop: "1px solid #E2E8F0",
  background: "#FAFAFA",
  gap: 12,
  flexWrap: "wrap",
};
const ctPagTxt = {
  fontSize: 12,
  color: "#6B7280",
  fontFamily: REPORT_FONT,
  whiteSpace: "nowrap",
};
const ctPSzSel = {
  height: 28,
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 12,
  color: "#374151",
  padding: "0 6px",
  background: "#fff",
  cursor: "pointer",
  fontFamily: REPORT_FONT,
};
const ctPBtn = (d) => ({
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  background: "#fff",
  color: d ? "#94A3B8" : "#374151",
  fontSize: 12,
  cursor: d ? "default" : "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: REPORT_FONT,
  opacity: d ? 0.35 : 1,
});
const ctPBtnA = {
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: `1px solid ${TH_BG_BAND}`,
  borderRadius: 6,
  background: TH_BG_BAND,
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: REPORT_FONT,
};
const ctPDots = {
  fontSize: 12,
  color: "#6B7280",
  padding: "0 4px",
  userSelect: "none",
};

const SCENARIO_FILTERS = [
  "year",
  "contractType",
  "contractId",
  "clinLvl2",
  "monthName",
  "quarter",
  "orgId",
  "businessType",
  "awardType",
  "competitionType",
  "bu",
  "popEndDate",
  "projectId",
];

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  /* ── page shell (matches WaterfallEAC) ── */
  page: {
    backgroundColor: "#F4F5F7",
    fontFamily: REPORT_FONT,
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },
  topBar: {
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "#6B7280" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: "#4F46E5",
    background: "#4F46E5",
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },

  /* ── tab bar (matches WaterfallEAC) ── */
  tabBar: {
    background: "#fff",
    borderBottom: `1px solid ${BORDER}`,
    display: "flex",
    flexWrap: "wrap",
    flexShrink: 0,
    padding: "0 24px",
    gap: "4px 0",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 500,
    fontFamily: REPORT_FONT,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "none",
    marginBottom: -1,
    transition: "color 0.15s, border-color 0.15s",
    "&:hover": { color: TAB_ACTIVE },
  },
  tabActive: {
    color: TAB_ACTIVE,
    fontWeight: 700,
    borderBottom: `2px solid ${TAB_ACTIVE}`,
  },

  headerContainer: { margin: "20px 0 0", padding: "0 16px" },

  /* ── body ── */
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    padding: "0 16px",
    overflowY: "auto",
    minWidth: 0,
  },

  /* ── horizontal filter bar ── */
  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: "1px solid #E5E7EB",
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: REPORT_FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: "1px solid #4F46E5", borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: "#4F46E5",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
  "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: REPORT_FONT,
    "&:hover": { color: "#4F46E5", borderColor: "#4F46E5" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: REPORT_FONT,
    "&:hover": { background: "#111827" },
  },

  /* ── active filter chips ── */
  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: "1px solid #E5E7EB",
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel: { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94A3B8",
    fontSize: 10,
    padding: 0,
    marginLeft: 2,
    "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  chipsClearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 6px",
    marginLeft: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: "#4F46E5" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },

  /* ── table card ── */
  tableCard: {
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    overflow: "hidden",
    marginBottom: 20,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
  },
  tableCardHead: {
    padding: "16px 20px 12px",
    borderBottom: "1px solid #E2E8F0",
    background: "#FFFFFF",
  },
  tableCardTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    letterSpacing: "-0.01em",
    fontFamily: REPORT_FONT,
    margin: 0,
  },
  tableCardMeta: {
    fontSize: 12,
    fontWeight: 400,
    color: "#6B7280",
    fontFamily: REPORT_FONT,
    lineHeight: 1.4,
    marginTop: 2,
  },
  tableWrap: { overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: REPORT_FONT,
    fontSize: "12px",
    tableLayout: "auto",
  },

  /* ── header cells (ByLcat palette) ── */
  thBlank: {
    background: HEADER_BG,
    borderRight: "1px solid rgba(255,255,255,.08)",
    borderBottom: "1px solid rgba(255,255,255,.12)",
    padding: "4px",
    position: "sticky",
    left: 0,
    zIndex: 3,
  },
  thYearLabel: {
    background: HEADER_BG,
    color: "rgba(255,255,255,.6)",
    padding: "5px 8px",
    fontSize: "10px",
    fontWeight: 600,
    textAlign: "center",
    borderRight: "1px solid rgba(255,255,255,.1)",
    borderBottom: "1px solid rgba(255,255,255,.12)",
    whiteSpace: "nowrap",
    position: "sticky",
    left: 380,
    zIndex: 3,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thRevBand: {
    background: HEADER_BG,
    color: "#fff",
    padding: "6px 10px",
    fontWeight: 700,
    fontSize: "11px",
    textAlign: "center",
    borderRight: "1px solid rgba(255,255,255,.1)",
    lineHeight: 1.4,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thDLBand: {
    background: HEADER_DL,
    color: "rgba(255,255,255,.9)",
    padding: "6px 10px",
    fontWeight: 700,
    fontSize: "11px",
    textAlign: "center",
    borderLeft: "2px solid rgba(255,255,255,.25)",
    borderRight: "1px solid rgba(255,255,255,.08)",
    lineHeight: 1.4,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.18)",
  },
  thFixed: {
    background: HEADER_COL,
    color: "rgba(255,255,255,.85)",
    padding: "7px 10px",
    fontWeight: 700,
    fontSize: "10px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "left",
    whiteSpace: "nowrap",
    borderRight: "1px solid rgba(255,255,255,.12)",
    borderBottom: "1px solid rgba(255,255,255,.1)",
    position: "sticky",
    zIndex: 3,
  },
  thMonth: {
    background: HEADER_COL,
    color: "rgba(255,255,255,.85)",
    padding: "6px 8px",
    fontWeight: 600,
    fontSize: "10px",
    textAlign: "right",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    borderRight: "1px solid rgba(255,255,255,.1)",
    borderBottom: "1px solid rgba(255,255,255,.1)",
    minWidth: "96px",
  },
  thMonthDL: {
    background: HEADER_DL,
    color: "rgba(255,255,255,.8)",
    padding: "6px 8px",
    fontWeight: 600,
    fontSize: "10px",
    textAlign: "right",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    borderRight: "1px solid rgba(255,255,255,.08)",
    borderBottom: "1px solid rgba(255,255,255,.08)",
    minWidth: "96px",
  },
  thBandTotal: {
    background: HEADER_COL,
    color: "#fff",
    padding: "6px 10px",
    fontWeight: 700,
    fontSize: "10px",
    textAlign: "right",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    borderLeft: "2px solid rgba(255,255,255,.25)",
    borderRight: "1px solid rgba(255,255,255,.1)",
    borderBottom: "1px solid rgba(255,255,255,.1)",
    minWidth: "108px",
  },
  thBandTotalDL: {
    background: HEADER_DL,
    color: "#fff",
    padding: "6px 10px",
    fontWeight: 700,
    fontSize: "10px",
    textAlign: "right",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    borderLeft: "2px solid rgba(255,255,255,.2)",
    borderBottom: "1px solid rgba(255,255,255,.08)",
    minWidth: "108px",
  },

  /* ── total row (ByLcat green band) ── */
  tdTotalLabel: {
    padding: "6px 10px",
    fontWeight: 700,
    color: "#111827",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #CBD5E1",
    borderRight: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#F0FFF4",
    position: "sticky",
    left: 0,
    zIndex: 1,
  },
  tdTotalSticky: {
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    borderRight: "1px solid #E2E8F0",
    position: "sticky",
    zIndex: 1,
  },
  tdTotalNum: {
    padding: "6px 8px",
    textAlign: "right",
    fontWeight: 600,
    color: "#111827",
    borderBottom: "1px solid #CBD5E1",
    borderRight: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#F0FFF4",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  tdTotalBandTotal: {
    padding: "6px 10px",
    textAlign: "right",
    fontWeight: 700,
    color: "#111827",
    borderBottom: "1px solid #CBD5E1",
    borderLeft: "2px solid #A7C5A0",
    borderRight: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#E6FFF1",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },

  /* ── data row cells ── */
  tdContractId: {
    padding: "6px 10px",
    fontWeight: 600,
    color: TH_BG,
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    borderRight: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 0,
    zIndex: 1,
    minWidth: "80px",
  },
  tdContractName: {
    padding: "6px 10px",
    fontWeight: 400,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    borderRight: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 80,
    zIndex: 1,
    minWidth: "220px",
  },
  tdContractType: {
    padding: "6px 10px",
    fontWeight: 400,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    borderRight: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 300,
    zIndex: 1,
    minWidth: "80px",
  },
  tdPopEndDate: {
    padding: "6px 10px",
    fontWeight: 400,
    color: "#64748B",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    borderRight: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 380,
    zIndex: 1,
    minWidth: "90px",
    boxShadow: "4px 0 6px -2px rgba(0,0,0,0.06)",
  },
  tdMonth: {
    padding: "6px 8px",
    textAlign: "right",
    color: "#334155",
    borderBottom: "1px solid #E2E8F0",
    borderRight: "1px solid #E2E8F0",
    fontSize: "12px",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  tdRowBandTotal: {
    padding: "6px 10px",
    textAlign: "right",
    fontWeight: 600,
    color: "#111827",
    borderBottom: "1px solid #E2E8F0",
    borderLeft: "2px solid #C8D0DC",
    borderRight: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#F8FAFC",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },

  /* ── pill-style filter row (CompareForecastVersions) ── */
  filterRow: {
    display: "flex",
    alignItems: "center",
    padding: "14px 20px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    background: "#FFFFFF",
    gap: 10,
  },
  filterGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 36,
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    flexShrink: 0,
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": {
      borderColor: "#C7D2FE",
      boxShadow: "0 3px 10px rgba(74,85,162,0.12)",
    },
    "&:focus-within": {
      borderColor: "#A5B4FC",
      boxShadow: "0 0 0 3px rgba(240,242,255,0.9)",
    },
  },
  filterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4A55A2",
    padding: "0 14px",
    background: "#F0F2FF",
    height: "100%",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    userSelect: "none",
    fontFamily: REPORT_FONT,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  filterSelect: {
    border: "none",
    outline: "none",
    backgroundColor: "#FFFFFF",
    fontSize: 13,
    fontWeight: 400,
    color: "#111111",
    padding: "0 28px 0 14px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: 80,
    height: "100%",
    fontFamily: REPORT_FONT,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748B' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
  },

  /* ── placeholder ── */
  card: {
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    overflow: "hidden",
    marginBottom: 20,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
  },
  cardHeader: {
    padding: "16px 20px 12px",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    background: "#fff",
  },
  placeholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "64px 24px",
    gap: "12px",
    color: "#94A3B8",
  },
  placeholderTitle: { fontSize: "16px", fontWeight: 600, color: "#64748B" },
  placeholderSub: { fontSize: "13px" },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  Icons                                                         */
/* ══════════════════════════════════════════════════════════════ */
function FilterIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
    </svg>
  );
}
function GlobalFilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}
function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Inline table styles — ByPerson pattern                        */
/* ══════════════════════════════════════════════════════════════ */
const TOTAL_FG = CELL_FG_STRONG;
const TOTAL_BG_SC = TOTAL_BG;
const TOTAL_BG2_SC = TOTAL_BG_ALT;
const ROW_BTOTAL_BG = ROW_TOTAL_BG;

const GH = 34,
  CH = 34,
  RH = 36;
const COL_W = 108;

const SC_ACCENT = ACCENT_COLOR;

const FIXED_COLS_SC = [
  { key: "contractId", label: "Contract ID", w: 100, align: "left", sticky: true },
  { key: "contractName", label: "Contract Name", w: 160, align: "left", sticky: true },
  { key: "contractType", label: "Type", w: 64, align: "left", sticky: true },
  { key: "popEndDate", label: "POP End", w: 88, align: "left", sticky: true },
];
let _scLeft = 0;
FIXED_COLS_SC.forEach((c) => {
  c.left = c.sticky ? _scLeft : 0;
  if (c.sticky) _scLeft += c.w;
});
const STICKY_W_SC = _scLeft;
const FIXED_W_SC = FIXED_COLS_SC.reduce((s, c) => s + c.w, 0);
const LAST_STICKY_KEY_SC = [...FIXED_COLS_SC].reverse().find((c) => c.sticky)?.key;

const TH_BG_SC = TH_BG;
const TH_TXT_SC = TH_TEXT;
const TH_BDR_SC = TH_BORDER;
const TH_SH_SC = `inset -1px 0 0 ${TH_BDR_SC}, inset 0 -1px 0 ${TH_BDR_SC}`;
const TH_SH_ROW1_SC = `inset -1px 0 0 ${TH_BDR_SC}`;
const TH_ROW2_TOP_BORDER = `1px solid ${TH_BDR_SC}`;

const thCoreSC = (top, h, z, padding, row = 2) => ({
  position: "sticky",
  top,
  zIndex: z,
  height: h,
  lineHeight: 1.25,
  boxSizing: "border-box",
  background: TH_BG_SC,
  color: TH_TXT_SC,
  verticalAlign: "middle",
  padding,
  whiteSpace: "nowrap",
  overflow: "hidden",
  boxShadow: row === 1 ? TH_SH_ROW1_SC : TH_SH_SC,
  border: "none",
  ...(row === 2 ? { borderTop: TH_ROW2_TOP_BORDER } : {}),
});

const thFixedBlankGroupSC = () => ({
  ...thCoreSC(0, GH, Z_HEAD + 1, 0, 1),
  left: 0,
  ...dynamicColSize(STICKY_W_SC),
});

const thFixedColSC = (col, isLastSticky) => ({
  ...thCoreSC(GH, CH, Z_HEAD + 1, "6px 6px", 2),
  ...(col.sticky ? { left: col.left, ...dynamicColSize(col.w) } : { ...dynamicColSize(col.w) }),
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.3,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: col.align,
  ...(col.sticky && isLastSticky
    ? {
        boxShadow: `4px 0 8px -2px rgba(0,0,0,0.12), ${TH_SH_SC}`,
        borderTop: TH_ROW2_TOP_BORDER,
      }
    : {}),
});

const thScrollFixedColSC = (col) => ({
  ...thMonthColSC(),
  ...dynamicColSize(col.w),
  textAlign: col.align,
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.3,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontWeight: 700,
  fontSize: 10,
  padding: "6px 6px",
});

const thBandGroupSC = () => ({
  ...thCoreSC(0, GH, Z_HEAD, "6px 8px", 1),
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  textAlign: "center",
});

const thMonthColSC = (isActual = false) => ({
  ...thCoreSC(GH, CH, Z_HEAD, "6px 4px", 2),
  background: isActual ? TH_ACTUAL_BG : TH_BG_SC,
  color: isActual ? TH_ACTUAL_TEXT : TH_TXT_SC,
  fontSize: 10,
  fontWeight: isActual ? 700 : 600,
  textAlign: "right",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  ...dynamicColSize(COL_W),
});

const thBandTotalColSC = () => ({
  ...thMonthColSC(),
  fontWeight: 700,
  color: "#F59E0B",
  ...dynamicColSize(COL_W),
});

const tdBaseSC = (bg, align = "right", fw = 400, isNumeric = true) => ({
  boxSizing: "border-box",
  background: bg,
  textAlign: align,
  fontWeight: fw,
  padding: "6px 4px",
  fontSize: 12,
  color: CELL_FG,
  ...cellWhiteSpaceStyle(isNumeric),
  verticalAlign: "middle",
  fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
});

const tdFixedSC = (bg, col, fw = 400, isLastSticky = false) => ({
  ...tdBaseSC(bg, col.align, fw, false),
  ...(col.sticky ? {
    position: "sticky",
    left: col.left,
    zIndex: Z_STICKY,
    boxShadow: isLastSticky ? "4px 0 8px -2px rgba(0,0,0,0.06)" : undefined,
  } : {}),
  ...dynamicColSize(col.w),
});

const tdScrollFixedSC = (bg, col, fw = 400) => ({
  ...tdBaseSC(bg, col.align, fw, false),
  ...dynamicColSize(col.w),
});

const tdTotalSC = (col, isLastSticky = false) => ({
  ...tdFixedSC(TOTAL_BG_SC, col, 700, isLastSticky),
  color: TOTAL_FG,
  borderBottom: "1px solid #CBD5E1",
});

const tdTotalScrollSC = () => ({
  ...tdBaseSC(TOTAL_BG_SC, "right", 600, true),
  color: TOTAL_FG,
  borderBottom: "1px solid #CBD5E1",
  ...dynamicColSize(COL_W),
});

const CARD_STYLE_SC = {
  background: "#FFFFFF",
  borderRadius: 12,
  border: "1px solid #EBEBEB",
  overflow: "hidden",
  maxWidth: "100%",
  marginBottom: 20,
  boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
};
const CARD_HEAD_STYLE_SC = {
  padding: "16px 20px 12px",
  borderBottom: "1px solid #E2E8F0",
  background: "#FFFFFF",
};
const CARD_TITLE_STYLE_SC = {
  fontSize: 14,
  fontWeight: 600,
  color: "#111827",
  letterSpacing: "-0.01em",
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  margin: 0,
};
const CARD_META_STYLE_SC = {
  fontSize: 12,
  fontWeight: 400,
  color: "#6B7280",
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  lineHeight: 1.4,
  marginTop: 2,
};

/* ══════════════════════════════════════════════════════════════ */
/*  Shared contract table — with pagination + column filters      */
/* ══════════════════════════════════════════════════════════════ */
function ContractTable({
  title,
  subtitle,
  totalRow,
  rows,
  formatter = fmtRev,
  year = "2026",
  showBandTotal = true,
  visibleBands = DEFAULT_VISIBLE_BANDS,
  controls,
  periodMonthKeys,
  actualColumns,
  actualThroughMonth,
}) {
  const activeBands = METRIC_BANDS.filter((b) => visibleBands.includes(b.key));
  const activeMonths = useMemo(() => {
    if (!periodMonthKeys?.length) return SC_MONTHS;
    const allowed = new Set(periodMonthKeys);
    return SC_MONTHS.filter((m) => allowed.has(m.key));
  }, [periodMonthKeys]);
  const actMonthSet = useMemo(() => {
    const s = new Set();
    const monthKeys = new Set(SC_MONTHS.map((m) => m.key));
    if (actualColumns?.length) {
      actualColumns.forEach((c) => {
        const raw = String(c).toLowerCase();
        const matched = SC_MONTHS.find(
          (m) => raw === m.key || raw.startsWith(m.key),
        );
        s.add(matched ? matched.key : raw);
      });
    }
    const hasKnownMonth = [...s].some((k) => monthKeys.has(k));
    if (!hasKnownMonth) {
      const thru = Number(actualThroughMonth) || 0;
      for (let i = 0; i < thru && i < SC_MONTHS.length; i++) {
        s.add(SC_MONTHS[i].key);
      }
    }
    return s;
  }, [actualColumns, actualThroughMonth]);
  const bandCols = activeMonths.length + (showBandTotal ? 1 : 0);
  const TABLE_W = FIXED_W_SC + activeBands.length * bandCols * COL_W;
  const cellColor = (v, fmtFn, isBandTotal = false) => {
    const decimals = fmtFn === fmtPctScenario ? 2 : 0;
    if (isDisplayNegative(v, decimals)) return "#DC2626";
    if (isBandTotal) return "#F59E0B";
    return CELL_FG;
  };

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKeys, setSortKeys] = useState([]);
  const [filterText, setFilterText] = useState({ id: "", name: "" });
  const [colFilters, setColFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });
  const [openOpKey, setOpenOpKey] = useState(null);
  const [opPos, setOpPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const opBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  useEffect(() => {
    setPage(1);
  }, [rows, filterText, colFilters]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      ) {
        setOpenFilterKey(null);
        setOpenOpKey(null);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  /* Reposition filter / op panels on scroll (stay anchored to header buttons) */
  useEffect(() => {
    const reposition = () => {
      const fk = openFilterKeyRef.current;
      if (fk) {
        const btn = filterBtnRefs.current[fk];
        if (btn) setFilterPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
      }
      const ok = openOpKeyRef.current;
      if (ok) {
        const ob = opBtnRefs.current[ok];
        if (ob) setOpPos(calcOpDropdownPos(ob));
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, []);

  const handleSort = useCallback((colKey) => {
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);
  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  const getSortValue = useCallback((row, colKey) => {
    const fixed = FIXED_COLS_SC.find((c) => c.key === colKey);
    if (fixed) return row[colKey];
    const us = colKey.indexOf("_");
    if (us > 0) {
      const bKey = colKey.slice(0, us);
      const rest = colKey.slice(us + 1);
      const band = METRIC_BANDS.find((b) => b.key === bKey);
      if (band) {
        if (rest === "total")
          return bandTotalForMonths(band, row, activeMonths);
        return band.get(row, rest);
      }
    }
    return row[colKey];
  }, []);

  const getSortColumnLabel = useCallback(
    (colKey) => {
      const fixed = FIXED_COLS_SC.find((c) => c.key === colKey);
      if (fixed) return fixed.label;
      const us = colKey.indexOf("_");
      if (us > 0) {
        const bKey = colKey.slice(0, us);
        const rest = colKey.slice(us + 1);
        const band =
          activeBands.find((b) => b.key === bKey) ||
          METRIC_BANDS.find((b) => b.key === bKey);
        if (band) {
          if (rest === "total") return `${band.label} Total`;
          const m = activeMonths.find((mo) => mo.key === rest);
          return m ? `${band.label} ${m.label}` : colKey;
        }
      }
      return colKey;
    },
    [activeBands],
  );

  function hasFilterValue(key) {
    if (key === "id") return !!filterText.id;
    if (key === "name") return !!filterText.name;
    return !!colFilters[key]?.value;
  }

  function toggleFilter(key, btnEl) {
    if (openFilterKey === key) {
      setOpenFilterKey(null);
      return;
    }
    setFilterPos(calcFilterPos(btnEl, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  }

  function toggleOpDropdown(key) {
    if (openOpKey === key) {
      setOpenOpKey(null);
      return;
    }
    const btn = opBtnRefs.current[key];
    if (!btn) return;
    setOpPos(calcOpDropdownPos(btn));
    setOpenOpKey(key);
  }

  function renderFilterContent(key) {
    if (key === "id") {
      return (
        <TextFilterContent
          label="Contract ID"
          value={filterText.id}
          onChange={(v) => setFilterText((p) => ({ ...p, id: v }))}
          onClear={() => setFilterText((p) => ({ ...p, id: "" }))}
        />
      );
    }
    if (key === "name") {
      return (
        <TextFilterContent
          label="Contract Name"
          value={filterText.name}
          onChange={(v) => setFilterText((p) => ({ ...p, name: v }))}
          onClear={() => setFilterText((p) => ({ ...p, name: "" }))}
        />
      );
    }
    const di = key.lastIndexOf("-");
    const bKey = key.slice(0, di);
    const mKey = key.slice(di + 1);
    const band = METRIC_BANDS.find((b) => b.key === bKey);
    const mLbl = mKey.charAt(0).toUpperCase() + mKey.slice(1);
    const f = colFilters[key] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={`${band?.label} · ${mLbl}`}
        value={f.value}
        op={f.op}
        onValueChange={(v) => setColFilters((p) => ({ ...p, [key]: { ...f, value: v } }))}
        onClear={() => setColFilters((p) => { const n = { ...p }; delete n[key]; return n; })}
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => { opBtnRefs.current[key] = el; }}
      />
    );
  }

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const idQ = filterText.id.trim().toLowerCase();
      const nameQ = filterText.name.trim().toLowerCase();
      if (
        idQ &&
        !String(row.contractId || "")
          .toLowerCase()
          .includes(idQ)
      )
        return false;
      if (
        nameQ &&
        !String(row.contractName || "")
          .toLowerCase()
          .includes(nameQ)
      )
        return false;
      for (const [key, f] of Object.entries(colFilters)) {
        if (!f?.value) continue;
        const di = key.lastIndexOf("-");
        const bKey = key.slice(0, di);
        const mKey = key.slice(di + 1);
        const band = METRIC_BANDS.find((b) => b.key === bKey);
        if (band && !ctApplyNumFilter(band.get(row, mKey), f)) return false;
      }
      /* Hide rows that are entirely zero across every visible column — keep
         only rows with at least one non-zero value. */
      const hasNonZero = activeBands.some((band) =>
        activeMonths.some((m) => (Number(band.get(row, m.key)) || 0) !== 0),
      );
      if (!hasNonZero) return false;
      return true;
    });
  }, [rows, filterText, colFilters, activeBands, activeMonths]);

  const sortedRows = useMemo(() => {
    if (sortKeys.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) =>
      compareBySortKeys(a, b, sortKeys, getSortValue),
    );
  }, [filteredRows, sortKeys, getSortValue]);

  const total = sortedRows.length;
  const totPg = Math.max(1, Math.ceil(total / pageSize));
  const sp = Math.min(page, totPg);
  const paged = sortedRows.slice((sp - 1) * pageSize, sp * pageSize);
  const rs = total === 0 ? 0 : (sp - 1) * pageSize + 1;
  const re = Math.min(sp * pageSize, total);

  const pageNums = (() => {
    if (totPg <= 7) return Array.from({ length: totPg }, (_, i) => i + 1);
    const s = new Set([1, totPg, sp, sp - 1, sp + 1, sp - 2, sp + 2]);
    return [...s].filter((p) => p >= 1 && p <= totPg).sort((a, b) => a - b);
  })();

  const hasTableFilters = useMemo(
    () =>
      hasAnyTableFilters(
        filterText.id.trim(),
        filterText.name.trim(),
        Object.values(colFilters).some((f) => f?.value),
      ),
    [filterText, colFilters],
  );

  const displayTotalRow = useMemo(() => {
    if (!totalRow) return null;
    if (!hasTableFilters) return totalRow;
    return (
      aggregateScenarioMomRow(sortedRows, activeBands, activeMonths) ?? totalRow
    );
  }, [totalRow, hasTableFilters, sortedRows, activeBands, activeMonths]);

  const tableStyle = {
    borderCollapse: "collapse",
    borderSpacing: 0,
    tableLayout: "fixed",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 12,
    width: TABLE_W,
    minWidth: TABLE_W,
  };

  return (
    <div style={CARD_STYLE_SC}>
      <div
        style={{
          ...CARD_HEAD_STYLE_SC,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div>
          <h2 style={CARD_TITLE_STYLE_SC}>{title}</h2>
          {(subtitle || sortKeys.length > 0) && (
            <div style={CARD_META_STYLE_SC}>
              {subtitle}
              {sortKeys.length > 0 && (
                <span
                  style={{
                    marginLeft: subtitle ? 8 : 0,
                    color: SC_ACCENT,
                    fontWeight: 500,
                  }}
                >
                  {subtitle ? " · " : ""}sorted by{" "}
                  {sortKeys.map((s) => getSortColumnLabel(s.key)).join(", ")}
                </span>
              )}
            </div>
          )}
        </div>
        {controls}
      </div>
      <div style={{ overflowX: "auto", overflowY: "hidden", width: "100%", maxWidth: "100%" }}>
        <table style={tableStyle}>
          <colgroup>
            {FIXED_COLS_SC.map((c) => (
              <col key={c.key} style={dynamicColSize(c.w)} />
            ))}
            {activeBands.flatMap((band) => [
              ...activeMonths.map((m) => (
                <col key={`${band.key}_${m.key}`} style={dynamicColSize(COL_W)} />
              )),
              ...(showBandTotal
                ? [<col key={`${band.key}_total`} style={dynamicColSize(COL_W)} />]
                : []),
            ])}
          </colgroup>

          <thead style={{ background: TH_BG_SC }}>
            {/* ── Row 1: band group headers ── */}
            <tr>
              <th
                colSpan={FIXED_COLS_SC.filter((c) => c.sticky).length}
                style={thFixedBlankGroupSC()}
                aria-hidden
              />
              {FIXED_COLS_SC.filter((c) => !c.sticky).length > 0 && (
                <th
                  colSpan={FIXED_COLS_SC.filter((c) => !c.sticky).length}
                  style={thBandGroupSC()}
                  aria-hidden
                />
              )}
              {activeBands.map((band) => (
                <th key={band.key} colSpan={bandCols} style={thBandGroupSC()}>
                  {band.label} · {year}
                </th>
              ))}
            </tr>

            {/* ── Row 2: column labels + filter buttons ── */}
            <tr>
              {FIXED_COLS_SC.map((c) => {
                const filterKey =
                  c.key === "contractId"
                    ? "id"
                    : c.key === "contractName"
                      ? "name"
                      : null;
                const active = filterKey
                  ? openFilterKey === filterKey || hasFilterValue(filterKey)
                  : false;
                return (
                  <th
                    key={c.key}
                    style={{
                      ...(c.sticky
                        ? thFixedColSC(c, c.key === LAST_STICKY_KEY_SC)
                        : thScrollFixedColSC(c)),
                      cursor: "pointer",
                    }}
                    onClick={onThClick(c.key)}
                  >
                    {filterKey ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 4,
                          minWidth: 0,
                        }}
                      >
                        <WaterfallThLabel
                          label={c.label}
                          colKey={c.key}
                          sortKeys={sortKeys}
                          getLabel={getSortColumnLabel}
                          align={c.align}
                        />
                        <button
                          ref={(el) => {
                            filterBtnRefs.current[filterKey] = el;
                          }}
                          data-filter-btn
                          style={ctFBtnSt(active)}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFilter(filterKey, e.currentTarget);
                          }}
                        >
                          <FilterIcon />
                        </button>
                      </div>
                    ) : (
                      <WaterfallThLabel
                        label={c.label}
                        colKey={c.key}
                        sortKeys={sortKeys}
                        getLabel={getSortColumnLabel}
                        align={c.align}
                      />
                    )}
                  </th>
                );
              })}
              {activeBands.flatMap((band, bi) => {
                const isLastBand = bi === activeBands.length - 1;
                return [
                  ...activeMonths.map((m) => {
                    const fKey = `${band.key}-${m.key}`;
                    const colKey = `${band.key}_${m.key}`;
                    const isAct = actMonthSet.has(m.key);
                    const active =
                      openFilterKey === fKey || hasFilterValue(fKey);
                    return (
                      <th
                        key={colKey}
                        style={{ ...thMonthColSC(isAct), cursor: "pointer" }}
                        onClick={onThClick(colKey)}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 3,
                            minWidth: 0,
                          }}
                        >
                          <WaterfallThLabel
                            label={m.label}
                            colKey={colKey}
                            sortKeys={sortKeys}
                            getLabel={getSortColumnLabel}
                            align="right"
                            textColor={
                              isAct && openFilterKey !== fKey
                                ? TH_ACTUAL_TEXT
                                : undefined
                            }
                            tone={isAct ? "actual" : "default"}
                          />
                          <button
                            ref={(el) => {
                              filterBtnRefs.current[fKey] = el;
                            }}
                            data-filter-btn
                            style={ctFBtnSt(active, isAct)}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFilter(fKey, e.currentTarget);
                            }}
                          >
                            <FilterIcon />
                          </button>
                        </div>
                      </th>
                    );
                  }),
                  ...(showBandTotal
                    ? [
                        <th
                          key={`${band.key}_total`}
                          style={{
                            ...thBandTotalColSC(),
                            cursor: "pointer",
                            borderTop: TH_ROW2_TOP_BORDER,
                            ...(!isLastBand
                              ? { borderRight: `2px solid ${TH_BDR_SC}` }
                              : {}),
                          }}
                          onClick={onThClick(`${band.key}_total`)}
                        >
                          <WaterfallThLabel
                            label="Total"
                            colKey={`${band.key}_total`}
                            sortKeys={sortKeys}
                            getLabel={getSortColumnLabel}
                            align="right"
                          />
                        </th>,
                      ]
                    : []),
                ];
              })}
            </tr>
          </thead>

          <tbody>
            {/* ── Grand total row ── */}
            <tr style={{ height: RH }}>
              {FIXED_COLS_SC.map((c) => (
                <td
                  key={c.key}
                  style={tdTotalSC(c, c.key === LAST_STICKY_KEY_SC)}
                >
                  {c.key === "contractId" ? "Total" : ""}
                </td>
              ))}
              {activeBands.flatMap((band, bi) => {
                const isLastBand = bi === activeBands.length - 1;
                const fmtFn = band.fmt || formatter;
                const totalVal = bandTotalForMonths(
                  band,
                  displayTotalRow,
                  activeMonths,
                );
                return [
                  ...activeMonths.map((m) => {
                    const val = band.get(displayTotalRow, m.key);
                    return (
                      <td
                        key={`${band.key}_${m.key}`}
                        style={{
                          ...tdTotalScrollSC(),
                          color: cellColor(val, fmtFn),
                        }}
                      >
                        {fmtFn(val)}
                      </td>
                    );
                  }),
                  ...(showBandTotal
                    ? [
                        <td
                          key={`${band.key}_total`}
                          style={{
                            ...tdTotalScrollSC(),
                            background: TOTAL_BG2_SC,
                            fontWeight: 700,
                            color: cellColor(totalVal, fmtFn, true),
                            ...(!isLastBand
                              ? { borderRight: "2px solid #A7C5A0" }
                              : {}),
                          }}
                        >
                          {fmtFn(totalVal)}
                        </td>,
                      ]
                    : []),
                ];
              })}
            </tr>

            {/* ── Paged data rows ── */}
            {paged.map((row, i) => {
              const bg = i % 2 === 0 ? EVEN_BG : ODD_BG;
              return (
                <tr key={row.contractId + i} style={{ height: RH }}>
                  {FIXED_COLS_SC.map((c) => (
                    <td
                      key={c.key}
                      style={
                        c.sticky
                          ? tdFixedSC(
                              bg,
                              c,
                              c.key === "contractId" ? 600 : 400,
                              c.key === LAST_STICKY_KEY_SC,
                            )
                          : tdScrollFixedSC(bg, c, c.key === "contractId" ? 600 : 400)
                      }
                      title={String(row[c.key] ?? "")}
                    >
                      {row[c.key] ?? ""}
                    </td>
                  ))}
                  {activeBands.flatMap((band, bi) => {
                    const isLastBand = bi === activeBands.length - 1;
                    const fmtFn = band.fmt || formatter;
                    const totalVal = bandTotalForMonths(
                      band,
                      row,
                      activeMonths,
                    );
                    return [
                      ...activeMonths.map((m) => {
                        const v = band.get(row, m.key);
                        return (
                          <td
                            key={`${band.key}_${m.key}`}
                            style={{
                              ...tdBaseSC(bg, "right", 400, true),
                              ...dynamicColSize(COL_W),
                              color: cellColor(v, fmtFn),
                            }}
                          >
                            {fmtFn(v)}
                          </td>
                        );
                      }),
                      ...(showBandTotal
                        ? [
                            <td
                              key={`${band.key}_total`}
                              style={{
                                ...tdBaseSC(ROW_BTOTAL_BG, "right", 700, true),
                                ...dynamicColSize(COL_W),
                                color: cellColor(totalVal, fmtFn, true),
                                ...(!isLastBand
                                  ? { borderRight: "2px solid #C8D0DC" }
                                  : {}),
                              }}
                            >
                              {fmtFn(totalVal) || ""}
                            </td>,
                          ]
                        : []),
                    ];
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {total > 0 && (
        <div style={ctPagBar}>
          <span style={ctPagTxt}>{`${rs}–${re} of ${total} contracts`}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={ctPagTxt}>Per page</span>
            <select
              style={ctPSzSel}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <button
                style={ctPBtn(sp === 1)}
                disabled={sp === 1}
                onClick={() => setPage(1)}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M9 10L6 7l3-3M5 10V4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                style={ctPBtn(sp === 1)}
                disabled={sp === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M8.5 10.5L5 7l3.5-3.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {pageNums.map((p, i) => (
                <React.Fragment key={p}>
                  {i > 0 && pageNums[i - 1] < p - 1 && (
                    <span style={ctPDots}>…</span>
                  )}
                  <button
                    style={p === sp ? ctPBtnA : ctPBtn(false)}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}
              <button
                style={ctPBtn(sp === totPg)}
                disabled={sp === totPg}
                onClick={() => setPage((p) => p + 1)}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M5.5 3.5L9 7l-3.5 3.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                style={ctPBtn(sp === totPg)}
                disabled={sp === totPg}
                onClick={() => setPage(totPg)}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M5 10l3-3-3-3M9 10V4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter panel portal */}
      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            style={{ ...fpBase, top: filterPos.top, left: filterPos.left }}
          >
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body,
        )}

      {/* Operator dropdown portal */}
      {openOpKey &&
        createPortal(
          <OpDropdownContent
            activeOp={(colFilters[openOpKey] || { op: "gt" }).op}
            onSelect={(op) => {
              setColFilters((p) => ({
                ...p,
                [openOpKey]: { ...(p[openOpKey] || { value: "" }), op },
              }));
              setOpenOpKey(null);
            }}
            style={{ top: opPos.top, left: opPos.left }}
          />,
          document.body,
        )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Compare Forecast Versions tab                                 */
/* ══════════════════════════════════════════════════════════════ */
const VERSION_DATA = {
  Q1: { total: CMP_Q1_TOTAL, rows: CMP_Q1_DATA },
  Q2: { total: Q2_TOTAL, rows: Q2_DATA },
};

/* ══════════════════════════════════════════════════════════════ */
/*  Placeholder tab                                               */
/* ══════════════════════════════════════════════════════════════ */
function PlaceholderTab({ label, classes }) {
  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>{label}</div>
      <div className={classes.placeholder}>
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#CBD5E1"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="9" y1="21" x2="9" y2="9" />
        </svg>
        <span className={classes.placeholderTitle}>{label}</span>
        <span className={classes.placeholderSub}>Content coming soon</span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Horizontal filter bar                                         */
/* ══════════════════════════════════════════════════════════════ */
function HorizontalFilterBar({
  visibleKeys,
  draft,
  dropdownOptions,
  onChange,
  onClearAll,
  onApply,
  onClose,
  classes,
  clearAllDisabled,
  applyDisabled,
}) {
  const filters = FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key));
  const selectStyle = { minWidth: 110, maxWidth: 180, fontSize: 12 };
  return (
    <div className={classes.hFilterBar}>
      {filters.map((f) => {
        const opts = toFilterSelectOptions(
          f.key,
          getFilterFieldOptions(f, dropdownOptions),
        );
        const val = draft[f.key];
        return (
          <div key={f.key} className={classes.hFilterGroup}>
            <span className={classes.hFilterLabel}>{f.label}</span>
            {f.type === "select" && (
              <Select
                size="small"
                style={selectStyle}
                value={val || (f.key === "year" ? opts[0]?.value : "All")}
                onChange={(v) =>
                  onChange(f.key, v === "All" ? f.defaultValue : v)
                }
                options={
                  f.key === "year"
                    ? opts
                    : [{ value: "All", label: "All" }, ...opts]
                }
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "multiselect" && (
              <GlobalMultiSelectFilter
                size="small"
                style={selectStyle}
                value={val}
                options={opts}
                onChange={(v) => onChange(f.key, v)}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "text" && (
              <input
                className={classes.hFilterInput}
                value={val || ""}
                placeholder="All"
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
            {f.type === "daterange" && (
              <HorizontalDateRangeFilter
                value={val}
                onChange={(next) => onChange(f.key, next)}
              />
            )}
          </div>
        );
      })}
      <div className={classes.hFilterActions}>
        <button
          className={classes.hFilterClearAll}
          onClick={onClearAll}
          disabled={clearAllDisabled}
        >
          Clear All
        </button>
        <FilterApplyButton
          draft={draft}
          onApply={onApply}
          className={classes.hFilterApply}
          disabled={applyDisabled}
        >
          Apply
        </FilterApplyButton>
      </div>
      <button
        className={classes.hFilterClose}
        onClick={onClose}
        title="Close Filters"
      >
        ✕
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  API response → flat row helpers                               */
/* ══════════════════════════════════════════════════════════════ */
const MEASURE_TO_PREFIX = {
  contractRevenue: "rev",
  directLabor: "dl",
  subcontractor: "subk",
  odcs: "odc",
  totalDirectCost: "tdc",
  grossMargin: "gp",
  grossMarginPct: "gmpct",
  indirectCost: "indirect",
  totalCost: "burden",
  burdenedCost: "burden",
  netProfit: "np",
  netProfitFee: "np",
  netMarginPct: "nmpct",
};

function flattenRow(apiRow) {
  const flat = {
    contractId: apiRow.id,
    contractName: apiRow.name,
    contractType: apiRow.contractType,
    popEndDate: apiRow.popEndDate,
  };
  Object.entries(apiRow.measures || {}).forEach(([mKey, monthMap]) => {
    const prefix = MEASURE_TO_PREFIX[mKey];
    if (!prefix) return;
    Object.entries(monthMap).forEach(([col, val]) => {
      flat[`${prefix}_${col}`] = val;
    });
  });
  return flat;
}

function flattenTotals(apiTotals) {
  const flat = {};
  Object.entries(apiTotals || {}).forEach(([mKey, monthMap]) => {
    const prefix = MEASURE_TO_PREFIX[mKey];
    if (!prefix) return;
    Object.entries(monthMap).forEach(([col, val]) => {
      flat[`${prefix}_${col}`] = val;
    });
  });
  return flat;
}

/* ══════════════════════════════════════════════════════════════ */
/*  Main export                                                   */
/* ══════════════════════════════════════════════════════════════ */
export default function ScenarioMoM() {
  const classes = useStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();
  const { tab: tabParam } = useParams();
  const activeTab = TABS.find((t) => t.key === tabParam)?.key ?? TABS[0].key;
  const [filterOpen, setFilterOpen] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(initFilterValues);
  const [draftFilters, setDraftFilters] = useState(initFilterValues);
  const [downloading, setDownloading] = useState(false);

  const visibleKeys = SCENARIO_FILTERS;
  const { dropdownOptions, relationshipRows, effectiveFilters } = useExpandedAppliedFilters(
    appliedFilters,
    draftFilters,
    visibleKeys,
  );

  const [bvcfLoading, setBvcfLoading] = useState(false);
  const [bvcfData, setBvcfData] = useState(null);

  const [pvcfLoading, setPvcfLoading] = useState(false);
  const [pvcfData, setPvcfData] = useState(null);

  const [nyLoading, setNyLoading] = useState(false);
  const [nyData, setNyData] = useState(null);

  const [cvLoading, setCvLoading] = useState(false);
  const [cvData, setCvData] = useState(null);
  const [cvVersion, setCvVersion] = useState(null);
  const [cvVersionOptions, setCvVersionOptions] = useState([]);

  const apiFilterKey = useMemo(
    () => getApiFilterKey(effectiveFilters, buildWaterfallEacApiFilters),
    [effectiveFilters],
  );
  const periodMonthKeys = useMemo(
    () => getGlobalPeriodColumnKeys(appliedFilters, "Month"),
    [appliedFilters?.monthName, appliedFilters?.quarter],
  );

  useEffect(() => {
    if (filterOpen) {
      setDraftFilters(
        pruneInvalidCascadeSelections(
          appliedFilters,
          relationshipRows,
          visibleKeys,
        ),
      );
    }
  }, [filterOpen, appliedFilters, relationshipRows, visibleKeys]);

  useEffect(() => {
    if (activeTab !== "budget_vs_forecast") return;
    setBvcfLoading(true);
    setBvcfData(null);
    getBudgetVsCurrentForecastAPI(buildWaterfallEacApiFilters(effectiveFilters))
      .then((res) => {
        if (res?.data) {
          const d = res.data;
          setBvcfData({
            cfData: (d.liveForecast || []).map(flattenRow),
            cfTotal: flattenTotals(d.liveForecastGrandTotals || {}),
            bdgData: (d.budget || []).map(flattenRow),
            bdgTotal: flattenTotals(d.budgetGrandTotals || {}),
            varData: (d.variance || []).map(flattenRow),
            varTotal: flattenTotals(d.varianceGrandTotals || {}),
            actualColumns: d.actualColumns || [],
            fiscalYear: d.fiscalYear,
            actualThroughMonth: d.actualThroughMonth,
          });
        }
      })
      .catch(() => {})
      .finally(() => setBvcfLoading(false));
  }, [activeTab, apiFilterKey]); // eslint-disable-line

  useEffect(() => {
    if (activeTab !== "prior_vs_forecast") return;
    setPvcfLoading(true);
    setPvcfData(null);
    getPriorVsCurrentForecastAPI(buildWaterfallEacApiFilters(effectiveFilters))
      .then((res) => {
        if (res?.data) {
          const d = res.data;
          setPvcfData({
            cfData: (d.liveForecast || []).map(flattenRow),
            cfTotal: flattenTotals(d.liveForecastGrandTotals || {}),
            q2Data: (d.priorForecast || []).map(flattenRow),
            q2Total: flattenTotals(d.priorForecastGrandTotals || {}),
            varData: (d.variance || []).map(flattenRow),
            varTotal: flattenTotals(d.varianceGrandTotals || {}),
            actualColumns: d.actualColumns || [],
            priorActualColumns: d.priorActualColumns || [],
            actualThroughMonth: d.actualThroughMonth,
            priorActualThroughMonth: d.priorActualThroughMonth,
            fiscalYear: d.fiscalYear,
            priorVersion: d.priorVersion,
          });
        }
      })
      .catch(() => {})
      .finally(() => setPvcfLoading(false));
  }, [activeTab, apiFilterKey]); // eslint-disable-line

  useEffect(() => {
    if (activeTab !== "next_year_forecast") return;
    setNyLoading(true);
    setNyData(null);
    getScenarioMomNextYearForecastAPI(
      buildWaterfallEacApiFilters(effectiveFilters),
    )
      .then((res) => {
        if (res?.data) {
          const d = res.data;
          setNyData({
            nyRows: (d.nextYearForecast || []).map(flattenRow),
            nyTotal: flattenTotals(d.nextYearForecastGrandTotals || {}),
            fiscalYear: d.fiscalYear,
          });
        }
      })
      .catch(() => {})
      .finally(() => setNyLoading(false));
  }, [activeTab, apiFilterKey]); // eslint-disable-line

  useEffect(() => {
    if (activeTab !== "compare_versions") return;
    if (cvVersionOptions.length === 0) {
      getVersionsAPI(appliedFilters.year)
        .then((opts) => {
          const list = (Array.isArray(opts?.data) ? opts.data : []).filter(
            (v) => v.key !== "liveEac" && !isBudgetVersion(v),
          );
          setCvVersionOptions(list);
          setCvVersion((prev) =>
            list.some((v) => v.key === prev)
              ? prev
              : resolveDefaultCompareVersion(list),
          );
        })
        .catch(() => {
          setCvVersion("");
        });
    }
  }, [activeTab]); // eslint-disable-line

  useEffect(() => {
    if (activeTab !== "compare_versions") return;
    /* Wait until a compare version is chosen — calling the API with no
       rightVersion lets the backend pick its own default (often Q3), which
       then flashes before the client settles on Q1. */
    if (!cvVersion) return;
    setCvLoading(true);
    setCvData(null);
    getCompareForecastVersionsAPI(
      buildWaterfallEacApiFilters(effectiveFilters),
      cvVersion,
    )
      .then((res) => {
        if (res?.data) {
          const d = res.data;
          setCvData({
            cfRows: (d.leftForecast || []).map(flattenRow),
            cfTotal: flattenTotals(d.leftForecastGrandTotals || {}),
            rightRows: (d.rightForecast || []).map(flattenRow),
            rightTotal: flattenTotals(d.rightForecastGrandTotals || {}),
            varRows: (d.variance || []).map(flattenRow),
            varTotal: flattenTotals(d.varianceGrandTotals || {}),
            actualColumns: d.actualColumns || [],
            rightActualColumns: d.rightActualColumns || [],
            leftVersion: d.leftVersion,
            rightVersion: d.rightVersion,
            fiscalYear: d.fiscalYear,
            actualThroughMonth: d.actualThroughMonth,
            rightActualThroughMonth: d.rightActualThroughMonth,
          });
        }
      })
      .catch(() => {})
      .finally(() => setCvLoading(false));
  }, [activeTab, apiFilterKey, cvVersion]); // eslint-disable-line

  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = FILTERS_CONFIG.filter(
    (f) =>
      visibleKeys.includes(f.key) && isFilterActive(f, appliedFilters[f.key]),
  );

  const isOnlyDefaultYear = (fv) => {
    const keys = visibleKeys.filter((k) => k !== "year");
    return (
      !keys.some((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        return f && isFilterActive(f, fv[k]);
      }) && String(fv.year) === "2026"
    );
  };

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    setAppliedFilters((prev) => {
      const next = { ...prev };
      visibleKeys.forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
    setDraftFilters((prev) => {
      const next = { ...prev };
      visibleKeys.forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, []); // eslint-disable-line

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) =>
        applyCascadeDraftChange(
          prev,
          key,
          value,
          relationshipRows,
          visibleKeys,
        ),
      );
    },
    [relationshipRows, visibleKeys],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters(
      pruneInvalidCascadeSelections(
        draftFilters,
        relationshipRows,
        visibleKeys,
      ),
    );
  }, [draftFilters, relationshipRows, visibleKeys]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      visibleKeys.forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, []); // eslint-disable-line

  const MONTH_ABBR = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const actualsThroughLabel = (data) => {
    const month = Number(data?.actualThroughMonth);
    if (!month || month < 1 || month > 12) return "";
    const year = data?.fiscalYear || new Date().getFullYear();
    return `Actuals through ${MONTH_ABBR[month - 1]} ${year}`;
  };

  const TAB_HEADER = {
    budget_vs_forecast: {
      eyebrow: "Scenario Comparison MoM",
      title: "Budget Vs Current Forecast",
      accent: bvcfData?.fiscalYear
        ? `FY-${bvcfData.fiscalYear} Budget Vs Forecast`
        : "Budget Vs Forecast",
      badge: "Live Forecast",
      date: actualsThroughLabel(bvcfData),
    },
    prior_vs_forecast: {
      eyebrow: "Scenario Comparison MoM",
      title: "Prior Vs Current Forecast",
      accent: pvcfData?.fiscalYear
        ? `FY-${pvcfData.fiscalYear} Prior Vs Forecast`
        : "Prior Vs Forecast",
      badge: "Live Forecast",
      date: actualsThroughLabel(pvcfData),
    },
    next_year_forecast: {
      eyebrow: "Scenario Comparison MoM",
      title: "Next Year Forecast",
      accent: nyData?.fiscalYear
        ? `FY-${nyData.fiscalYear} Outlook`
        : "Next Year Outlook",
      badge: "Live Forecast",
      date: actualsThroughLabel(nyData),
    },
    compare_versions: {
      eyebrow: "Scenario Comparison MoM",
      title: "Compare Forecast Versions",
      accent: cvData?.rightVersion?.name
        ? `${cvData?.leftVersion?.name || "Live EAC"} Vs ${cvData.rightVersion.name}`
        : "Version Comparison",
      badge: "Live Forecast",
      date: actualsThroughLabel(cvData),
    },
  };

  const renderContent = () => {
    if (activeTab === "budget_vs_forecast") {
      if (bvcfLoading) return <Loading />;
      return (
        <BudgetVsCurrentForecastTabComponent
          classes={classes}
          ContractTable={ContractTable}
          periodMonthKeys={periodMonthKeys}
          actualColumns={bvcfData?.actualColumns ?? []}
          actualThroughMonth={bvcfData?.actualThroughMonth}
          CF_TOTAL={bvcfData?.cfTotal ?? {}}
          CF_DATA={bvcfData?.cfData ?? []}
          BDG_TOTAL={bvcfData?.bdgTotal ?? {}}
          BDG_DATA={bvcfData?.bdgData ?? []}
          VAR_TOTAL={bvcfData?.varTotal ?? {}}
          VAR_DATA={bvcfData?.varData ?? []}
          fmtVar={fmtVar}
          liveTitle={`Current Month Actuals + Live Forecast FY-${bvcfData?.fiscalYear || 2026}`}
          fiscalYear={bvcfData?.fiscalYear}
        />
      );
    }
    if (activeTab === "prior_vs_forecast") {
      if (pvcfLoading) return <Loading />;
      return (
        <PriorVsCurrentForecastTabComponent
          classes={classes}
          ContractTable={ContractTable}
          periodMonthKeys={periodMonthKeys}
          actualColumns={pvcfData?.actualColumns ?? []}
          priorActualColumns={pvcfData?.priorActualColumns ?? []}
          actualThroughMonth={pvcfData?.actualThroughMonth}
          priorActualThroughMonth={pvcfData?.priorActualThroughMonth}
          CF_TOTAL={pvcfData?.cfTotal ?? {}}
          CF_DATA={pvcfData?.cfData ?? []}
          Q2_TOTAL={pvcfData?.q2Total ?? {}}
          Q2_DATA={pvcfData?.q2Data ?? []}
          PRIOR_VAR_TOTAL={pvcfData?.varTotal ?? {}}
          PRIOR_VAR_DATA={pvcfData?.varData ?? []}
          liveTitle={`Current Month Actuals + Live Forecast FY-${pvcfData?.fiscalYear || 2026}`}
          priorTitle={
            pvcfData?.priorVersion?.name
              ? `Current Month Actuals + ${pvcfData.priorVersion.name}`
              : "Current Month Actuals + Prior Forecast"
          }
          priorVersionName={pvcfData?.priorVersion?.name}
          fmtVar={fmtVar}
          fiscalYear={pvcfData?.fiscalYear}
        />
      );
    }
    if (activeTab === "next_year_forecast") {
      if (nyLoading) return <Loading />;
      return (
        <NextYearForecastTabComponent
          classes={classes}
          ContractTable={ContractTable}
          periodMonthKeys={periodMonthKeys}
          nyTotal={nyData?.nyTotal ?? {}}
          nyRows={nyData?.nyRows ?? []}
          fiscalYear={nyData?.fiscalYear}
        />
      );
    }
    if (activeTab === "compare_versions") {
      if (cvLoading || cvVersion == null) return <Loading />;
      return (
        <CompareForecastVersionsTabComponent
          classes={classes}
          ContractTable={ContractTable}
          periodMonthKeys={periodMonthKeys}
          cfTotal={cvData?.cfTotal ?? {}}
          cfRows={cvData?.cfRows ?? []}
          rightTotal={cvData?.rightTotal ?? {}}
          rightRows={cvData?.rightRows ?? []}
          varTotal={cvData?.varTotal ?? {}}
          varRows={cvData?.varRows ?? []}
          actualColumns={cvData?.actualColumns ?? []}
          rightActualColumns={cvData?.rightActualColumns ?? []}
          actualThroughMonth={cvData?.actualThroughMonth}
          rightActualThroughMonth={cvData?.rightActualThroughMonth}
          leftVersion={cvData?.leftVersion}
          rightVersionMeta={cvData?.rightVersion}
          fiscalYear={cvData?.fiscalYear}
          versions={cvVersionOptions}
          rightVersion={cvVersion}
          onVersionChange={setCvVersion}
          fmtVar={fmtVar}
        />
      );
    }
    const label = TABS.find((t) => t.key === activeTab)?.label ?? "";
    return <PlaceholderTab label={label} classes={classes} />;
  };

  const handleDownload = useCallback(async () => {
    const entry = DOWNLOAD_BY_TAB[activeTab];
    if (!entry || downloading) return;
    setDownloading(true);
    try {
      const apiFilters = buildWaterfallEacDownloadFilters(effectiveFilters);
      const { blob, filename } =
        activeTab === "compare_versions"
          ? await entry.api(apiFilters, cvVersion)
          : await entry.api(apiFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || entry.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Scenario Comparison MoM download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [activeTab, effectiveFilters, cvVersion, downloading]);

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {/* ── Navigation bar ── */}
      <div className={classes.topBar}>
        <BackButton
          onClick={() => goToReportsCatalog(history)}
          ariaLabel="Back to Reports"
        />
        <nav className={classes.crumb}>
          <span
            className={classes.crumbLink}
            onClick={() => goToReportsCatalog(history)}
          >
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>Scenario Comparison MoM</span>
        </nav>
        <div className={classes.topActions}>
          <button
            type="button"
            className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
            title="Download as Excel"
            aria-label="Download Report"
            onClick={handleDownload}
            disabled={downloading}
          >
            <DownloadIcon />
          </button>
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <GlobalFilterIcon />
            Filters
            {filterCount > 0 && (
              <span className={classes.filterBadge}>{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() =>
              history.push(`/projects/reports/scenario_mom/${tab.key}`)
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <HorizontalFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, visibleKeys)}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isOnlyDefaultYear(draftFilters)}
        />
      )}

      {/* ── Active filter chips ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${f.label}: ${val.join(", ")}`
                : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${f.label}`}
                  disabled={f.key === "year" && String(val) === "2026"}
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            className={classes.chipsClearAll}
            onClick={clearAllApplied}
            disabled={isOnlyDefaultYear(appliedFilters)}
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── Hero header ── */}
      <div className={classes.headerContainer}>
        <PlPageHeader
          classes={plClasses}
          eyebrow={TAB_HEADER[activeTab].eyebrow}
          title={TAB_HEADER[activeTab].title}
          accent={TAB_HEADER[activeTab].accent}
          badge={TAB_HEADER[activeTab].badge}
          date={TAB_HEADER[activeTab].date}
        />
      </div>

      {/* ── Body ── */}
      <div className={classes.body}>
        <div className={classes.content}>{renderContent()}</div>
      </div>
    </div>
  );
}
